import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
    Download, Upload, Plus, Trash2, Users, ZoomIn, ZoomOut, RotateCcw, Search, User,
    Undo, Redo, Save, Moon, Sun, Hash, Grid3x3, Heart, Sparkles, Palette, Minus,
    ChevronDown, Check, X, Settings, Shuffle, Circle, Music, Utensils, AlertTriangle,
    Share2, Eye, EyeOff, ListChecks, UserCheck, UserPlus, Table, Camera, Coffee,
    Home, Speaker, Headphones, Mic, Radio, Calendar, Clock, Crown, Target, MapPin,
    Shirt, Diamond, Car, Plane, Gift, Flower, Bell, Filter,
    TrendingUp, Award, Flame, Timer, BarChart3, Edit3, Phone, Mail, Baby,
    Hotel, Send, PartyPopper, Tag, ChevronRight, Star, Zap, List, CheckCircle,
    ArrowUp, ArrowDown, TableProperties, RotateCw, Move, Copy, Maximize2,
    Volume2, DollarSign, Image, Disc, Gamepad,
    Sofa, TreePine, Snowflake, Megaphone, Wifi, Power,
    MousePointer, Link, Layers, Type, AlignCenter,
    MoreHorizontal, ChevronLeft, RefreshCw, Maximize, Lock, Unlock, BrainCircuit,
    ArrowDownToLine, ArrowRightToLine, MinusSquare, Pin, PinOff, Notebook, LayoutGrid, Info,
    Briefcase, Link2, Unlink
} from 'lucide-react';

// FIREBASE IMPORTS
import { initializeApp } from "firebase/app";
import { getFirestore, doc, setDoc, onSnapshot, getDoc, collection, addDoc } from "firebase/firestore";
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, updateProfile } from "firebase/auth";

// FIREBASE CONFIGURATION
// WAŻNE: Wstaw tutaj swoje prawdziwe dane konfiguracyjne Firebase.
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "YOUR_API_KEY",
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "weddinger2025.firebaseapp.com",
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "weddinger2025",
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "weddinger2025.appspot.com",
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "1085203227628",
    appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:1085203227628:web:69ff0f9e6eb4f59e0316c9"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);

// #region CORE APPLICATION STATE
const LEGACY_DATA_COLLECTION = "weddingData";
const LEGACY_DATA_DOC = "mainStateV3";
const AUTO_CLOUD_BACKUP_INTERVAL_MS = 6 * 60 * 60 * 1000;

const createEmptyAppState = () => ({
    weddingSettings: {
        brideName: "",
        groomName: "",
        weddingDate: "",
        totalBudget: 0,
        yourName: "",
        yourRole: "couple",
        expectedGuests: 0,
        weddingStyle: "",
        weddingLocation: "",
    },
    guests: [],
    tables: [
        { id: 'head-table', name: 'PARA MŁODA', x: 700, y: 200, seats: 2, shape: 'rectangle', width: 200, height: 80, rotation: 0, color: '#f59e0b', radius: null, isLocked: false },
    ],
    decorations: [
        { id: 'dance-floor', name: 'Parkiet Taneczny', x: 700, y: 1000, width: 300, height: 250, type: 'dance-floor', color: '#8b5cf6', rotation: 0, isLocked: false },
    ],
    tasks: [],
    vendors: [],
    expenses: [],
    notes: [],
    activeSection: 'dashboard',
});

const mergeWithDefaultAppState = (data) => {
    const defaultState = createEmptyAppState();
    const source = data || {};
    return {
        ...defaultState,
        ...source,
        weddingSettings: {
            ...defaultState.weddingSettings,
            ...(source.weddingSettings || {}),
        },
        guests: Array.isArray(source.guests) ? source.guests : defaultState.guests,
        tables: Array.isArray(source.tables) ? source.tables : defaultState.tables,
        decorations: Array.isArray(source.decorations) ? source.decorations : defaultState.decorations,
        tasks: Array.isArray(source.tasks) ? source.tasks : defaultState.tasks,
        vendors: Array.isArray(source.vendors) ? source.vendors : defaultState.vendors,
        expenses: Array.isArray(source.expenses) ? source.expenses : defaultState.expenses,
        notes: Array.isArray(source.notes) ? source.notes : defaultState.notes,
    };
};

const createStarterTasks = (weddingDate) => {
    const today = new Date().toISOString().split('T')[0];
    const baseDate = weddingDate || today;
    return [
        {
            id: crypto.randomUUID(),
            title: 'Ustal styl i klimat wesela',
            completed: false,
            category: 'other',
            assignee: 'both',
            priority: 'high',
            dueDate: baseDate,
            cost: 0,
            createdAt: new Date().toISOString(),
            completedAt: null,
            tags: ['start'],
        },
        {
            id: crypto.randomUUID(),
            title: 'Stwórz wstępną listę gości',
            completed: false,
            category: 'venue',
            assignee: 'both',
            priority: 'high',
            dueDate: baseDate,
            cost: 0,
            createdAt: new Date().toISOString(),
            completedAt: null,
            tags: ['goście'],
        },
        {
            id: crypto.randomUUID(),
            title: 'Zaplanuj budżet głównych kategorii',
            completed: false,
            category: 'catering',
            assignee: 'both',
            priority: 'medium',
            dueDate: baseDate,
            cost: 0,
            createdAt: new Date().toISOString(),
            completedAt: null,
            tags: ['budżet'],
        },
    ];
};

const buildStateFromOnboarding = (formData) => {
    const expectedGuests = Math.max(parseInt(formData.expectedGuests || 0, 10), 0);
    const extraTablesCount = Math.max(Math.ceil(expectedGuests / 8), 0);
    const extraTables = Array.from({ length: extraTablesCount }).map((_, index) => ({
        id: `table-${index + 1}`,
        name: `Stół ${index + 1}`,
        x: 380 + (index % 3) * 260,
        y: 460 + Math.floor(index / 3) * 220,
        seats: 8,
        shape: 'circle',
        rotation: 0,
        color: '#3b82f6',
        radius: 80,
        width: null,
        height: null,
        isLocked: false,
    }));

    const baseState = createEmptyAppState();
    return {
        ...baseState,
        weddingSettings: {
            ...baseState.weddingSettings,
            yourName: formData.yourName?.trim() || "",
            yourRole: formData.yourRole || "couple",
            brideName: formData.brideName?.trim() || "",
            groomName: formData.groomName?.trim() || "",
            weddingDate: formData.weddingDate || "",
            totalBudget: Number(formData.totalBudget || 0),
            expectedGuests,
            weddingStyle: formData.weddingStyle?.trim() || "",
            weddingLocation: formData.weddingLocation?.trim() || "",
        },
        tables: [...baseState.tables, ...extraTables],
        tasks: createStarterTasks(formData.weddingDate),
        notes: [
            {
                id: crypto.randomUUID(),
                title: "Wizja wesela",
                content: `Styl: ${formData.weddingStyle || 'Do ustalenia'}\nMiejsce: ${formData.weddingLocation || 'Do ustalenia'}\nPriorytet: Komfort gości i spokojna organizacja.`,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            },
        ],
    };
};
// #endregion

// #region UTILITY HOOKS
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
};

const useFilteredGuests = (guests, guestListView, searchTerm) => {
    return useMemo(() => {
        let filtered = guests || [];

        if (guestListView === 'unassigned') {
            filtered = guests.filter(g => !g.tableId && g.wedding);
        } else if (guestListView === 'by-tables') {
            filtered = guests.filter(g => g.tableId);
        } else if (guestListView === 'no-wedding') {
            filtered = guests.filter(g => g.wedding === false);
        } else { // 'all' view
             filtered = guests;
        }
        
        if (!searchTerm) return filtered;
        
        const lowercasedTerm = searchTerm.toLowerCase();
        return filtered.filter(guest => {
            const guestFullName = `${guest.firstName} ${guest.lastName}`;
            return guestFullName.toLowerCase().includes(lowercasedTerm) ||
                (guest.email && guest.email.toLowerCase().includes(lowercasedTerm)) ||
                (guest.notes && guest.notes.toLowerCase().includes(lowercasedTerm)) ||
                (guest.tags && guest.tags.some(tag => tag.toLowerCase().includes(lowercasedTerm)));
        });
    }, [guests, guestListView, searchTerm]);
};
// #endregion

// #region CORE UI COMPONENTS
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    componentDidCatch(error, errorInfo) {
        console.error("Uncaught error:", error, errorInfo);
        this.props.showNotification(`Wystąpił krytyczny błąd: ${error.message}`, 'error', 10000);
    }
    render() {
        if (this.state.hasError) {
            return (
                <div className="w-full h-screen flex items-center justify-center bg-red-50 dark:bg-gray-900 text-red-700 dark:text-red-300 p-4">
                    <div className="text-center">
                        <AlertTriangle size={48} className="mx-auto mb-4" />
                        <h1 className="text-2xl font-bold">Coś poszło nie tak.</h1>
                        <p className="mt-2">Spróbuj odświeżyć stronę. Błąd został zapisany w konsoli.</p>
                        <p className="mt-2 text-xs bg-red-100 dark:bg-red-900/50 p-2 rounded">{this.state.error?.message}</p>
                    </div>
                </div>
            )
        }
        return this.props.children;
    }
}

const CustomConfetti = ({ isFiring }) => {
    const canvasRef = useRef(null);
    const animationFrameId = useRef();

    const createParticles = useCallback((canvas) => {
        const particles = [];
        const particleCount = 200;
        const colors = ["#fde047", "#f97316", "#ec4899", "#8b5cf6", "#3b82f6"];
        for (let i = 0; i < particleCount; i++) {
            particles.push({
                x: canvas.width / 2,
                y: canvas.height,
                vx: (Math.random() - 0.5) * 15,
                vy: Math.random() * -30 - 15,
                size: Math.random() * 5 + 2,
                color: colors[Math.floor(Math.random() * colors.length)],
                life: 300,
            });
        }
        return particles;
    }, []);

    const animate = useCallback((particles, ctx, canvas) => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        particles.forEach((p, index) => {
            p.x += p.vx;
            p.y += p.vy;
            p.vy += 0.4; // Gravity
            p.life--;

            if (p.life <= 0) {
                particles.splice(index, 1);
            } else {
                ctx.fillStyle = p.color;
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
                ctx.fill();
            }
        });

        if (particles.length > 0) {
            animationFrameId.current = requestAnimationFrame(() => animate(particles, ctx, canvas));
        }
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (isFiring && canvas) {
            const ctx = canvas.getContext('2d');
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            
            const particles = createParticles(canvas);
            
            if(animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }
            animate(particles, ctx, canvas);
        }

        return () => {
            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }
        };
    }, [isFiring, animate, createParticles]);

    if (!isFiring) return null;

    return <canvas ref={canvasRef} style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', pointerEvents: 'none', zIndex: 9999 }} />;
};

const QuickHelpModal = ({ isOpen, onClose, onDisableAutoOpen }) => {
    const [step, setStep] = useState(0);
    const steps = [
        {
            title: 'Szybki start',
            description: 'Najpierw ustaw dane ślubu w Ustawieniach (ikona koła zębatego), potem dodaj gości i zadania.',
            points: [
                'Dashboard pokazuje postęp całości.',
                'W każdej zakładce zapis jest automatyczny.',
                'W razie czego masz backup i import jednym kliknięciem.',
            ],
        },
        {
            title: 'Plan stołów',
            description: 'W zakładce Plan Stołów możesz budować salę dokładnie tak, jak chcesz.',
            points: [
                'Dodawaj stoły i elementy sali.',
                'Przeciągaj, obracaj, zmieniaj rozmiar.',
                'Możesz zresetować cały układ sali jednym przyciskiem.',
            ],
        },
        {
            title: 'Goście i pary',
            description: 'W Liście Gości dodajesz osoby ręcznie lub z CSV i możesz łączyć je w pary.',
            points: [
                'Kliknij ikonę łączenia, żeby połączyć dwie osoby.',
                'Kliknij ikonę rozłączenia, żeby cofnąć parę.',
                'Każdy gość może mieć status: potwierdzony, odmowa lub brak odpowiedzi.',
            ],
        },
        {
            title: 'Bezpieczeństwo danych',
            description: 'Masz kilka warstw bezpieczeństwa, żeby nie stracić danych.',
            points: [
                'Ikona archiwum: ręczna kopia zapasowa.',
                'Ikona strzałki w dół: pobranie pełnego backupu JSON.',
                'Ikona strzałki w górę: przywrócenie backupu JSON.',
            ],
        },
    ];

    useEffect(() => {
        if (!isOpen) setStep(0);
    }, [isOpen]);

    if (!isOpen) return null;

    const currentStep = steps[step];
    const isFirstStep = step === 0;
    const isLastStep = step === steps.length - 1;

    return (
        <div className="fixed inset-0 z-[270] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-2xl border border-gray-200 dark:border-gray-700 max-w-xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-rose-500 font-bold">Pomoc</p>
                        <h3 className="text-2xl font-black text-gray-900 dark:text-gray-100 mt-1">{currentStep.title}</h3>
                    </div>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700">
                        <X size={18} />
                    </button>
                </div>

                <p className="text-sm text-gray-700 dark:text-gray-200 mb-4">{currentStep.description}</p>

                <div className="space-y-2 mb-6">
                    {currentStep.points.map((point) => (
                        <div key={point} className="text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg px-3 py-2">
                            {point}
                        </div>
                    ))}
                </div>

                <div className="flex items-center gap-1 mb-5">
                    {steps.map((item, index) => (
                        <div
                            key={item.title}
                            className={`h-1.5 rounded-full transition-all ${index === step ? 'w-8 bg-rose-500' : 'w-4 bg-gray-300 dark:bg-gray-700'}`}
                        />
                    ))}
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={onDisableAutoOpen}
                        className="text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    >
                        Nie pokazuj automatycznie
                    </button>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setStep((prev) => Math.max(0, prev - 1))}
                            disabled={isFirstStep}
                            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-semibold disabled:opacity-40"
                        >
                            Wstecz
                        </button>
                        {!isLastStep ? (
                            <button
                                type="button"
                                onClick={() => setStep((prev) => Math.min(steps.length - 1, prev + 1))}
                                className="px-4 py-2 rounded-lg bg-gradient-to-r from-rose-500 to-orange-500 text-white text-sm font-bold"
                            >
                                Dalej
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 rounded-lg bg-gradient-to-r from-rose-500 to-orange-500 text-white text-sm font-bold"
                            >
                                Zamknij
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};


const MainHeader = React.memo(({ darkMode, setDarkMode, dashboardStats, onSettingsClick, onOpenHelp, weddingSettings, currentUserName, onSignOut, onExportData, onImportData, isImportingData }) => {
    const importFileInputRef = useRef(null);

    const handleImportClick = () => {
        importFileInputRef.current?.click();
    };

    const handleImportFileChange = (event) => {
        const file = event.target.files?.[0];
        if (file) onImportData(file);
        event.target.value = '';
    };

    return (
    <header className="z-40 bg-cream-50/80 dark:bg-gray-900/80 backdrop-blur-2xl border-b border-gray-200/50 dark:border-gray-800/50">
        <div className="max-w-screen-2xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="relative">
                        <Heart className="w-10 h-10 text-rose-500 dark:text-rose-400" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black tracking-tight bg-gradient-to-r from-rose-500 via-purple-500 to-indigo-500 bg-clip-text text-transparent dark:from-rose-400 dark:via-purple-400 dark:to-indigo-400">
                            Weddinger
                        </h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                            {weddingSettings.brideName && weddingSettings.groomName 
                                ? `${weddingSettings.brideName} & ${weddingSettings.groomName}`
                                : 'Twój asystent ślubny ✨'
                            }
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    <div className="hidden lg:flex items-center gap-2 px-3 py-2 bg-white/70 dark:bg-gray-800/60 rounded-xl border border-gray-200/60 dark:border-gray-700/60">
                        <User size={14} className="text-gray-500" />
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 max-w-[160px] truncate">
                            {currentUserName || 'Konto'}
                        </span>
                    </div>
                    <div className="hidden md:flex items-center gap-4 px-4 py-2 bg-cream-100/80 dark:bg-gray-800/50 rounded-2xl backdrop-blur-sm border border-gray-200/30 dark:border-gray-700/30">
                        <div className="flex items-center gap-2" title="Postęp zadań">
                            <Target size={16} className="text-emerald-500" />
                            <span className="text-sm font-bold">{dashboardStats.tasks.completion}%</span>
                        </div>
                        <div className="w-px h-4 bg-gray-300 dark:bg-gray-600" />
                        <div className="flex items-center gap-2" title="Goście potwierdzeni">
                            <Users size={16} className="text-blue-500" />
                            <span className="text-sm font-bold">{dashboardStats.guests.confirmed}/{dashboardStats.guests.total}</span>
                        </div>
                         <div className="w-px h-4 bg-gray-300 dark:bg-gray-600" />
                        <div className="flex items-center gap-2" title="Dni do ślubu">
                            <Calendar size={16} className="text-rose-500" />
                            <span className="text-sm font-bold">{dashboardStats.daysLeft}</span>
                        </div>
                    </div>
                    
                    <button
                        onClick={onSettingsClick}
                        className="p-3 rounded-2xl transition-all duration-300 focus:outline-none backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700/80 hover:shadow-lg hover:scale-105"
                        title="Ustawienia ślubu: imiona, data, budżet i podstawowe dane planera."
                    >
                        <Settings size={20} />
                    </button>

                    <button
                        onClick={onOpenHelp}
                        className="p-3 rounded-2xl transition-all duration-300 focus:outline-none backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700/80 hover:shadow-lg hover:scale-105"
                        title="Pomoc: szybki tutorial krok po kroku i opis najważniejszych funkcji."
                    >
                        <Info size={20} />
                    </button>

                    <button
                        onClick={() => setDarkMode(!darkMode)}
                        className="p-3 rounded-2xl transition-all duration-300 focus:outline-none backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700/80 hover:shadow-lg hover:scale-105"
                        title={darkMode ? "Przełącz na tryb jasny." : "Przełącz na tryb ciemny."}
                    >
                        {darkMode ? <Sun size={20} /> : <Moon size={20} />}
                    </button>

                    <button
                        onClick={onExportData}
                        className="p-3 rounded-2xl transition-all duration-300 focus:outline-none backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700/80 hover:shadow-lg hover:scale-105"
                        title="Pobierz pełny backup danych do pliku JSON."
                    >
                        <Download size={20} />
                    </button>

                    <button
                        onClick={handleImportClick}
                        disabled={isImportingData}
                        className="p-3 rounded-2xl transition-all duration-300 focus:outline-none backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700/80 hover:shadow-lg hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                        title="Wgraj backup JSON i przywróć dane planera."
                    >
                        <Upload size={20} />
                    </button>

                    <button
                        onClick={onSignOut}
                        className="p-3 rounded-2xl transition-all duration-300 focus:outline-none backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700/80 hover:shadow-lg hover:scale-105"
                        title="Wyloguj się z konta."
                    >
                        <Power size={20} />
                    </button>
                    <input
                        ref={importFileInputRef}
                        type="file"
                        accept="application/json,.json"
                        className="hidden"
                        onChange={handleImportFileChange}
                    />
                </div>
            </div>
        </div>
    </header>
    );
});

const NavigationTabs = React.memo(({ activeSection, setActiveSection, navigationItems }) => (
    <nav className="z-30 bg-cream-50/60 dark:bg-gray-900/60 backdrop-blur-lg border-b border-gray-200/30 dark:border-gray-800/30 shadow-sm">
        <div className="max-w-screen-2xl mx-auto px-6">
            <div className="flex items-center justify-center gap-2 md:gap-4">
                {navigationItems.map(item => {
                    const Icon = item.icon;
                    const isActive = activeSection === item.id;
                    return (
                        <button
                            key={item.id}
                            onClick={() => setActiveSection(item.id)}
                            title={item.hint || item.name}
                            className={`flex items-center gap-2.5 px-3 md:px-4 py-3 font-semibold transition-all duration-200 border-b-2 ${isActive
                                ? `border-rose-500 text-rose-600 dark:text-rose-400 dark:border-rose-400`
                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                            }`}
                        >
                            <Icon size={18} />
                            <span className="text-sm hidden sm:inline">{item.name}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    </nav>
));
// #endregion

// #region MODAL COMPONENTS
const SettingsModal = ({ isOpen, onClose, settings, setSettings, showNotification }) => {
    const [localSettings, setLocalSettings] = useState(settings);

    useEffect(() => {
        setLocalSettings(settings);
    }, [settings, isOpen]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setLocalSettings(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = () => {
        setSettings(localSettings);
        showNotification('Ustawienia zostały zapisane!', 'success');
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-2xl border border-gray-200 dark:border-gray-700 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2"><Settings className="text-rose-500"/> Ustawienia Wesela</h3>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"><X size={18} /></button>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="text-sm font-semibold text-gray-800 dark:text-gray-100">Imię Panny Młodej</label>
                        <input type="text" name="brideName" value={localSettings.brideName || ''} onChange={handleChange} placeholder="Anna" className="mt-1 w-full p-2.5 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg border border-gray-300 dark:border-gray-600 placeholder:text-gray-400" />
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-gray-800 dark:text-gray-100">Imię Pana Młodego</label>
                        <input type="text" name="groomName" value={localSettings.groomName || ''} onChange={handleChange} placeholder="Jan" className="mt-1 w-full p-2.5 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg border border-gray-300 dark:border-gray-600 placeholder:text-gray-400" />
                    </div>
                     <div>
                        <label className="text-sm font-semibold text-gray-800 dark:text-gray-100">Data ślubu</label>
                        <input type="date" name="weddingDate" value={localSettings.weddingDate || ''} onChange={handleChange} className="mt-1 w-full p-2.5 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg border border-gray-300 dark:border-gray-600 placeholder:text-gray-400" />
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-gray-800 dark:text-gray-100">Całkowity budżet (zł)</label>
                        <input type="number" name="totalBudget" value={localSettings.totalBudget || ''} onChange={handleChange} placeholder="50000" className="mt-1 w-full p-2.5 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg border border-gray-300 dark:border-gray-600 placeholder:text-gray-400" />
                    </div>
                </div>
                <button onClick={handleSave} className="w-full mt-6 flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-500 to-purple-500 text-white font-semibold rounded-lg shadow-lg hover:scale-105 transition-transform">
                    <Save size={16} /> Zapisz ustawienia
                </button>
            </div>
        </div>
    );
};

const AddGuestModal = ({ isOpen, onClose, setGuests, showNotification }) => {
    const [activeTab, setActiveTab] = useState('manual');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [tags, setTags] = useState('');
    const fileInputRef = useRef(null);

    if (!isOpen) return null;

    const handleManualAdd = (e) => {
        e.preventDefault();
        if (!firstName || !lastName) {
            showNotification('Proszę podać imię i nazwisko', 'error');
            return;
        }
        const newGuest = {
            id: crypto.randomUUID(),
            firstName,
            lastName,
            email: '', phone: '', notes: '', 
            tags: tags.split(',').map(tag => tag.trim()).filter(Boolean), 
            tableId: null, seatNumber: null,
            color: `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')}`,
            wedding: true, afterparty: null, accommodation: false, children: 0, menu: 'standard',
            transport: false, invitationSent: false, confirmed: null, plusOne: '',
            partnerId: null,
            tableAssigned: false, priority: 'medium', dietary: []
        };
        setGuests(prev => [...prev, newGuest]);
        showNotification(`${firstName} ${lastName} został dodany!`, 'success');
        setFirstName('');
        setLastName('');
        setTags('');
        onClose();
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file && file.type === "text/csv") {
            const reader = new FileReader();
            reader.onload = (e) => {
                const text = e.target.result;
                const rows = text.split('\n').slice(1); // Skip header
                const newGuests = rows.map((row) => {
                    const columns = row.split(',');
                    if (columns.length >= 2 && columns[0] && columns[1]) {
                        return {
                            id: crypto.randomUUID(),
                            firstName: columns[0].trim(),
                            lastName: columns[1].trim(),
                            tags: columns[2] ? columns[2].split(';').map(t => t.trim()) : [],
                            email: '', phone: '', notes: '', tableId: null, seatNumber: null,
                            color: `#${Math.floor(Math.random()*16777215).toString(16).padStart(6, '0')}`,
                            wedding: true, afterparty: null, accommodation: false, children: 0, menu: 'standard',
                            transport: false, invitationSent: false, confirmed: null, plusOne: '',
                            partnerId: null,
                            tableAssigned: false, priority: 'medium', dietary: []
                        };
                    }
                    return null;
                }).filter(Boolean); // Remove null entries

                if (newGuests.length > 0) {
                    setGuests(prev => [...prev, ...newGuests]);
                    showNotification(`Zaimportowano ${newGuests.length} gości!`, 'success');
                    onClose();
                } else {
                    showNotification('Nie znaleziono poprawnych danych w pliku CSV.', 'error');
                }
            };
            reader.readAsText(file, 'UTF-8');
        } else {
            showNotification('Proszę wybrać poprawny plik .csv', 'error');
        }
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-2xl border border-gray-200 dark:border-gray-700 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Dodaj Gości</h3>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"><X size={18} /></button>
                </div>
                <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-1 mb-4">
                    <button onClick={() => setActiveTab('manual')} className={`flex-1 px-2 py-1.5 text-sm font-bold rounded-lg transition-all ${activeTab === 'manual' ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-400'}`}>Ręcznie</button>
                    <button onClick={() => setActiveTab('csv')} className={`flex-1 px-2 py-1.5 text-sm font-bold rounded-lg transition-all ${activeTab === 'csv' ? 'bg-white dark:bg-gray-600 shadow-sm' : 'text-gray-600 dark:text-gray-400'}`}>Importuj z CSV</button>
                </div>
                {activeTab === 'manual' ? (
                    <form onSubmit={handleManualAdd} className="space-y-4">
                        <div>
                            <label className="text-sm font-semibold text-gray-800 dark:text-gray-100">Imię</label>
                            <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Jan" className="mt-1 w-full p-2.5 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg border border-gray-300 dark:border-gray-600 placeholder:text-gray-400" required />
                        </div>
                        <div>
                            <label className="text-sm font-semibold text-gray-800 dark:text-gray-100">Nazwisko</label>
                            <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Kowalski" className="mt-1 w-full p-2.5 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg border border-gray-300 dark:border-gray-600 placeholder:text-gray-400" required />
                        </div>
                         <div>
                            <label className="text-sm font-semibold text-gray-800 dark:text-gray-100">Tagi (oddzielone przecinkiem)</label>
                            <input type="text" value={tags} onChange={e => setTags(e.target.value)} placeholder="vip, rodzina, praca" className="mt-1 w-full p-2.5 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg border border-gray-300 dark:border-gray-600 placeholder:text-gray-400" />
                        </div>
                        <button type="submit" className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-lg shadow-lg">
                            <UserPlus size={16} /> Dodaj gościa
                        </button>
                    </form>
                ) : (
                    <div className="text-center">
                        <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">Plik .csv powinien zawierać kolumny: `Imię`, `Nazwisko`, `Tagi` (oddzielone średnikiem).</p>
                        <input type="file" accept=".csv" ref={fileInputRef} onChange={handleFileSelect} className="hidden" id="csv-upload" />
                        <label htmlFor="csv-upload" className="w-full cursor-pointer flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-violet-500 text-white font-semibold rounded-lg shadow-lg">
                            <Upload size={16} /> Wybierz plik CSV
                        </label>
                    </div>
                )}
            </div>
        </div>
    )
};
// #endregion

// #region --- DASHBOARD COMPONENTS ---
const StatCard = React.memo(({ title, value, subtitle, icon, gradient, progress, onClick }) => {
    const Icon = icon;
    return (
        <div
            onClick={onClick}
            className={`group relative overflow-hidden bg-white dark:bg-gray-800/60 backdrop-blur-xl rounded-3xl p-6 border border-cream-200/50 dark:border-gray-700/30 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:border-cream-300/50 dark:hover:border-gray-600/50 ${onClick ? 'cursor-pointer' : ''}`}
        >
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative flex items-start justify-between mb-4">
                <div className={`p-4 rounded-2xl bg-gradient-to-r ${gradient} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <Icon size={24} className="text-white" />
                </div>
                {progress !== undefined && (
                    <div className="text-right">
                        <div className="text-2xl font-black text-gray-900 dark:text-gray-100">{progress}%</div>
                        <div className="w-16 h-2 bg-cream-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                                className={`h-full bg-gradient-to-r ${gradient} transition-all duration-1000 ease-out`}
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>
                )}
            </div>
            <div className="relative">
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-1">{title}</h3>
                <div className="text-3xl font-black text-gray-900 dark:text-gray-100 mb-2">{value}</div>
                {subtitle && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 bg-cream-100 dark:bg-gray-700/30 px-3 py-1 rounded-full inline-block">
                        {subtitle}
                    </p>
                )}
            </div>
        </div>
    );
});

const QuickActionCard = React.memo(({ title, description, icon, gradient, onClick }) => {
    const Icon = icon;
    return (
        <button
            onClick={onClick}
            className={`group w-full text-left p-6 bg-gradient-to-r ${gradient} rounded-2xl text-white shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 active:scale-95`}
        >
            <div className="flex items-center justify-between mb-3">
                <Icon size={24} className="group-hover:scale-110 transition-transform" />
                <ChevronRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </div>
            <h3 className="text-lg font-bold mb-1">{title}</h3>
            <p className="text-sm text-white/80">{description}</p>
        </button>
    );
});

const Dashboard = ({ dashboardStats, setActiveSection, tasks }) => {
    return (
        <div className="space-y-8">
            <div className="relative overflow-hidden bg-gradient-to-br from-purple-600 via-rose-600 to-amber-500 rounded-3xl p-8 text-white shadow-2xl">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse"></div>
                <div className="relative">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm">
                            <Heart size={32} className="text-white" />
                        </div>
                        <div>
                            <h2 className="text-3xl font-black mb-2">Witaj w Weddinger!</h2>
                            <p className="text-lg text-white/90">Twoje wesele jest na dobrej drodze</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                            <div className="text-2xl font-black">{dashboardStats.tasks.completion}%</div>
                            <div className="text-sm text-white/80">Zadań ukończonych</div>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                            <div className="text-2xl font-black">{dashboardStats.guests.confirmed}</div>
                            <div className="text-sm text-white/80">Gości potwierdzonych</div>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                            <div className="text-2xl font-black">{dashboardStats.daysLeft}</div>
                            <div className="text-sm text-white/80">Dni do ślubu</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    title="Goście"
                    value={dashboardStats.guests.total}
                    subtitle={`${dashboardStats.guests.confirmed} potwierdzonych`}
                    icon={Users}
                    gradient="from-blue-500 to-cyan-500"
                    progress={dashboardStats.guests.total > 0 ? Math.round((dashboardStats.guests.confirmed / dashboardStats.guests.total) * 100) : 0}
                    onClick={() => setActiveSection('guests')}
                />
                <StatCard
                    title="Miejsca"
                    value={`${dashboardStats.seating.occupiedSeats}/${dashboardStats.seating.totalSeats}`}
                    subtitle={`${dashboardStats.seating.freeSeats} wolnych`}
                    icon={Table}
                    gradient="from-purple-500 to-violet-500"
                    progress={dashboardStats.seating.totalSeats > 0 ? Math.round((dashboardStats.seating.occupiedSeats / dashboardStats.seating.totalSeats) * 100) : 0}
                    onClick={() => setActiveSection('seating')}
                />
                <StatCard
                    title="Zadania"
                    value={`${dashboardStats.tasks.completed}/${dashboardStats.tasks.total}`}
                    subtitle="Na dobrej drodze"
                    icon={Target}
                    gradient="from-green-500 to-emerald-500"
                    progress={dashboardStats.tasks.completion}
                    onClick={() => setActiveSection('tasks')}
                />
                <StatCard
                    title="Budżet"
                    value={`${(dashboardStats.budget.spent / 1000).toFixed(0)}k zł`}
                    subtitle={`z ${(dashboardStats.budget.total / 1000).toFixed(0)}k zł`}
                    icon={TrendingUp}
                    gradient="from-orange-500 to-amber-500"
                    progress={dashboardStats.budget.progress}
                     onClick={() => setActiveSection('expenses')}
                />
            </div>

            <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-3">
                    <Zap className="text-yellow-500" />
                    Szybkie akcje
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <QuickActionCard
                        title="Dodaj gościa"
                        description="Szybko dodaj nowego gościa do listy"
                        icon={UserPlus}
                        gradient="from-blue-500 to-indigo-500"
                        onClick={() => setActiveSection('guests')}
                    />
                    <QuickActionCard
                        title="Ustawienia stołów"
                        description="Zarządzaj rozmieszczeniem gości"
                        icon={Table}
                        gradient="from-purple-500 to-pink-500"
                        onClick={() => setActiveSection('seating')}
                    />
                    <QuickActionCard
                        title="Nowe zadanie"
                        description="Dodaj kolejne zadanie do zrobienia"
                        icon={Plus}
                        gradient="from-green-500 to-teal-500"
                        onClick={() => setActiveSection('tasks')}
                    />
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800/60 backdrop-blur-xl rounded-2xl p-6 border border-cream-200/50 dark:border-gray-700/30 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-3">
                    <Clock className="text-indigo-500" />
                    Nadchodzące zadania
                </h3>
                <div className="space-y-3">
                    {tasks.filter(t => !t.completed).sort((a,b) => new Date(a.dueDate) - new Date(b.dueDate)).slice(0, 5).map(task => (
                        <div key={task.id} className="flex items-center gap-3 p-3 bg-cream-50 dark:bg-gray-700/30 rounded-xl">
                            <Circle size={16} className="text-gray-400" />
                            <span className="flex-1 text-sm text-gray-900 dark:text-gray-100">
                                {task.title}
                            </span>
                            <span className="text-xs text-gray-500">
                                {new Date(task.dueDate).toLocaleDateString('pl-PL')}
                            </span>
                        </div>
                    ))}
                     {tasks.filter(t => !t.completed).length === 0 && (
                        <p className="text-center text-gray-500 py-4">Brak nadchodzących zadań. Wszystko zrobione! 🎉</p>
                    )}
                </div>
            </div>
        </div>
    );
};
// #endregion

// #region --- TASK MANAGER COMPONENTS ---
const TaskItem = React.memo(({ task, toggleTask, updateTask, deleteTask, categories, shouldAutoEdit, onAutoEditHandled }) => {
    const { id, title, completed, category, dueDate } = task;
    const CategoryIcon = categories[category]?.icon || Target;
    const [isEditing, setIsEditing] = useState(false);
    const [editedTitle, setEditedTitle] = useState(title);
    const inputRef = useRef(null);

    useEffect(() => {
        if (isEditing) {
            inputRef.current?.focus();
            inputRef.current?.select();
        }
    }, [isEditing]);

    useEffect(() => {
        if (shouldAutoEdit && !completed) {
            setIsEditing(true);
            if (typeof onAutoEditHandled === 'function') onAutoEditHandled(id);
        }
    }, [completed, id, onAutoEditHandled, shouldAutoEdit]);

    const handleSave = () => {
        if (editedTitle.trim() !== '') {
            updateTask(id, { title: editedTitle.trim() });
        } else {
            setEditedTitle(title);
        }
        setIsEditing(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSave();
        else if (e.key === 'Escape') {
            setEditedTitle(title);
            setIsEditing(false);
        }
    };
    
    return (
        <div className={`group p-4 rounded-xl flex items-center gap-4 transition-all ${completed ? 'bg-cream-100 dark:bg-gray-800/50 opacity-60' : 'bg-white dark:bg-gray-800/80 shadow-lg border border-cream-200/80 dark:border-gray-700/50'}`}>
            <button onClick={() => toggleTask(id)} className="flex-shrink-0">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 transition-all duration-200 ${completed ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300 dark:border-gray-600 hover:border-emerald-400'}`}>
                    {completed && <Check size={16} className="text-white" />}
                </div>
            </button>
            <div className="flex-1" onDoubleClick={() => !completed && setIsEditing(true)}>
                {isEditing ? (
                   <input
                        ref={inputRef}
                        type="text"
                        value={editedTitle}
                        onChange={(e) => setEditedTitle(e.target.value)}
                        onBlur={handleSave}
                        onKeyDown={handleKeyDown}
                        className="w-full bg-transparent p-0 m-0 border-none focus:ring-0 text-gray-900 dark:text-gray-100"
                    />
                ) : (
                    <p className={`cursor-pointer ${completed ? 'line-through text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}>{title}</p>
                )}
                 <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 mt-1">
                    <div className="flex items-center gap-1.5">
                        <CategoryIcon size={12} style={{ color: categories[category]?.color }} />
                        {categories[category]?.name}
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Calendar size={12} />
                        {new Date(dueDate).toLocaleDateString('pl-PL')}
                    </div>
                 </div>
            </div>
            <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => setIsEditing(!isEditing)} className="p-2 rounded-lg hover:bg-cream-100 dark:hover:bg-gray-700/50 text-gray-500 dark:text-gray-400 transition-colors" title="Edytuj zadanie">
                    <Edit3 size={16} />
                </button>
                <button onClick={() => deleteTask(id)} className="p-2 rounded-lg hover:bg-rose-100 dark:hover:bg-rose-900/50 text-rose-500 dark:text-rose-400 transition-colors" title="Usuń zadanie">
                    <Trash2 size={16} />
                </button>
            </div>
        </div>
    );
});

const TasksManager = ({ tasks, setTasks, showNotification, toggleTask, updateTask, deleteTask, categories }) => {
    const [autoEditTaskId, setAutoEditTaskId] = useState(null);

    const handleAddTask = () => {
        const newTask = { id: crypto.randomUUID(), title: 'Nowe zadanie', completed: false, category: 'venue', assignee: 'both', priority: 'medium', dueDate: new Date().toISOString().split('T')[0], cost: 0, createdAt: new Date().toISOString(), tags: [] };
        setTasks(prev => [newTask, ...prev]);
        setAutoEditTaskId(newTask.id);
        showNotification('Dodano nowe zadanie!', 'success');
    };
    
    const tasksToDo = tasks.filter(t => !t.completed).sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
    const tasksCompleted = tasks.filter(t => t.completed).sort((a, b) => b.completedAt ? new Date(b.completedAt) - new Date(a.completedAt) : -1);

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-3xl font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">Organizer Zadań</h2>
                <button onClick={handleAddTask} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-xl font-semibold transition-all hover:scale-105 shadow-lg active:scale-95">
                    <Plus size={16} /> Dodaj zadanie
                </button>
            </div>
            <div>
                <h3 className="text-xl font-bold mb-4 mt-6">Do zrobienia ({tasksToDo.length})</h3>
                <div className="space-y-3">
                    {tasksToDo.map(task => (
                        <TaskItem
                            key={task.id}
                            task={task}
                            toggleTask={toggleTask}
                            updateTask={updateTask}
                            deleteTask={deleteTask}
                            categories={categories}
                            shouldAutoEdit={task.id === autoEditTaskId}
                            onAutoEditHandled={(taskId) => setAutoEditTaskId(prev => prev === taskId ? null : prev)}
                        />
                    ))}
                     {tasksToDo.length === 0 && <p className="text-center text-gray-500 py-8">Brak zadań do wykonania. Dobra robota!</p>}
                </div>
            </div>
            <div>
                <h3 className="text-xl font-bold mb-4 mt-8">Ukończone ({tasksCompleted.length})</h3>
                <div className="space-y-3">
                    {tasksCompleted.map(task => <TaskItem key={task.id} task={task} toggleTask={toggleTask} updateTask={updateTask} deleteTask={deleteTask} categories={categories} shouldAutoEdit={false} />)}
                </div>
            </div>
        </div>
    );
};
// #endregion

// #region --- NOTES MANAGER COMPONENTS ---
const NotesManager = ({ notes, setNotes }) => {
    const [activeNoteId, setActiveNoteId] = useState(notes && notes.length > 0 ? notes.sort((a,b) => new Date(b.updatedAt) - new Date(a.updatedAt))[0].id : null);
    const [isSaving, setIsSaving] = useState(false);
    const saveTimeout = useRef(null);

    const activeNote = useMemo(() => notes.find(n => n.id === activeNoteId), [notes, activeNoteId]);
    const debouncedContent = useDebounce(activeNote?.content, 1500);

    useEffect(() => {
        if (debouncedContent !== undefined && activeNote) {
            const originalNote = notes.find(n => n.id === activeNoteId);
            if (originalNote && debouncedContent !== originalNote.content) {
                if (saveTimeout.current) clearTimeout(saveTimeout.current);
                setIsSaving(true);
                saveTimeout.current = setTimeout(() => setIsSaving(false), 2000);
            }
        }
        return () => {
            if (saveTimeout.current) clearTimeout(saveTimeout.current);
        }
    }, [debouncedContent, activeNote, notes, activeNoteId]);

    const handleContentChange = (content) => {
        setNotes(prev => prev.map(n => n.id === activeNoteId ? { ...n, content, updatedAt: new Date().toISOString() } : n));
    };
    
    const addNote = () => {
        const newId = crypto.randomUUID();
        const newNote = {
            id: newId,
            title: "Nowa notatka",
            content: "",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };
        setNotes(prev => [newNote, ...prev]);
        setActiveNoteId(newId);
    };

    return (
        <div className="flex flex-col md:flex-row h-[calc(100vh-220px)] gap-6">
            <div className="md:w-1/3 w-full bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl border border-cream-200/50 dark:border-gray-700/50 shadow-lg overflow-hidden flex flex-col">
                <div className="p-4 border-b border-cream-200/50 dark:border-gray-700/50 flex justify-between items-center">
                    <h3 className="text-lg font-bold">Notatki</h3>
                    <button onClick={addNote} className="p-2 rounded-lg hover:bg-cream-100 dark:hover:bg-gray-700"><Plus size={16}/></button>
                </div>
                <div className="overflow-y-auto">
                    {notes.sort((a,b) => new Date(b.updatedAt) - new Date(a.updatedAt)).map(note => (
                        <button key={note.id} onClick={() => setActiveNoteId(note.id)} className={`w-full text-left p-4 border-l-4 ${activeNoteId === note.id ? 'border-rose-500 bg-cream-100 dark:bg-gray-700/50' : 'border-transparent hover:bg-cream-100/50 dark:hover:bg-gray-700/30'}`}>
                            <h4 className="font-bold truncate">{note.content.split('\n')[0] || note.title}</h4>
                            <p className="text-sm text-gray-500 truncate">{note.content.split('\n')[1] || "Brak dodatkowej treści"}</p>
                        </button>
                    ))}
                </div>
            </div>
            <div className="md:w-2/3 w-full bg-white dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl border border-cream-200/50 dark:border-gray-700/50 shadow-lg flex flex-col">
                {activeNote ? (
                    <>
                        <div className="p-4 border-b border-cream-200/50 dark:border-gray-700/50 flex justify-end items-center h-12">
                            <span className={`text-sm text-gray-500 transition-opacity duration-500 ${isSaving ? 'opacity-100' : 'opacity-0'}`}>Zapisano</span>
                        </div>
                        <textarea
                            key={activeNote.id}
                            value={activeNote.content}
                            onChange={(e) => handleContentChange(e.target.value)}
                            className="w-full h-full p-6 bg-transparent focus:outline-none resize-none text-base leading-7 dark:text-gray-200 text-gray-800"
                            placeholder="Zacznij pisać..."
                        />
                    </>
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                        <Notebook size={48} />
                        <p className="mt-4">Wybierz notatkę lub stwórz nową.</p>
                    </div>
                )}
            </div>
        </div>
    );
};
// #endregion

// #region --- EXPENSES MANAGER COMPONENTS ---
const ExpensesManager = ({ expenses, setExpenses, weddingSettings, showNotification }) => {
    const [newExpense, setNewExpense] = useState({ category: 'venue', name: '', cost: '' });
    
    const handleAddExpense = (e) => {
        e.preventDefault();
        if (!newExpense.name || !newExpense.cost) {
            showNotification('Wypełnij nazwę i koszt!', 'error');
            return;
        }
        const expenseToAdd = {
            id: crypto.randomUUID(),
            ...newExpense,
            cost: parseFloat(newExpense.cost),
            paid: false,
        };
        setExpenses(prev => [...prev, expenseToAdd].sort((a,b) => b.cost - a.cost));
        setNewExpense({ category: 'venue', name: '', cost: '' });
        showNotification('Dodano nowy wydatek!', 'success');
    };

    const deleteExpense = (id) => {
        setExpenses(prev => prev.filter(ex => ex.id !== id));
        showNotification("Wydatek usunięty.", "info");
    }

    const togglePaid = (id) => {
      setExpenses(prev => prev.map(ex => ex.id === id ? {...ex, paid: !ex.paid } : ex));
    }
    
    const totalSpent = useMemo(() => expenses.reduce((sum, ex) => sum + ex.cost, 0), [expenses]);
    const budgetProgress = weddingSettings.totalBudget > 0 ? Math.round((totalSpent / weddingSettings.totalBudget) * 100) : 0;

    return (
        <div className="space-y-6">
            <div className="p-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl text-white shadow-2xl">
                <h2 className="text-3xl font-black">Zarządzanie Budżetem</h2>
                <div className="mt-4 flex justify-between items-end">
                    <div>
                        <p className="text-lg">Wydano</p>
                        <p className="text-5xl font-black">{totalSpent.toLocaleString('pl-PL')} zł</p>
                    </div>
                    <div className="text-right">
                        <p className="text-lg">z</p>
                        <p className="text-2xl font-bold">{parseFloat(weddingSettings.totalBudget || 0).toLocaleString('pl-PL')} zł</p>
                    </div>
                </div>
                <div className="w-full bg-white/20 h-4 rounded-full mt-4 overflow-hidden">
                    <div className="h-full bg-white transition-all duration-1000 ease-out" style={{ width: `${Math.min(budgetProgress, 100)}%` }}></div>
                     {budgetProgress > 100 && <div className="h-full bg-red-500 transition-all duration-1000 ease-out" style={{ width: `${Math.min(budgetProgress - 100, 100)}%` }}></div>}
                </div>
            </div>
            
            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl border border-cream-200/50 dark:border-gray-700/50 shadow-lg p-6">
                <h3 className="text-xl font-bold mb-4">Dodaj nowy wydatek</h3>
                <form onSubmit={handleAddExpense} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                   <input type="text" placeholder="Nazwa wydatku (np. Fotograf)" value={newExpense.name} onChange={e => setNewExpense({...newExpense, name: e.target.value})} className="md:col-span-2 mt-1 w-full p-2 bg-cream-100 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600" />
                   <input type="number" placeholder="Koszt" value={newExpense.cost} onChange={e => setNewExpense({...newExpense, cost: e.target.value})} className="mt-1 w-full p-2 bg-cream-100 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600" />
                    <button type="submit" className="flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-lg shadow-lg">
                        <Plus size={16} /> Dodaj
                    </button>
                </form>
            </div>
             <div className="overflow-x-auto rounded-xl border border-cream-200/50 dark:border-gray-700/50 bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl shadow-lg">
                <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                     <thead className="text-xs text-gray-700 uppercase bg-cream-50/50 dark:bg-gray-700/50">
                        <tr>
                            <th scope="col" className="px-6 py-3">Nazwa</th>
                            <th scope="col" className="px-6 py-3">Koszt</th>
                            <th scope="col" className="px-6 py-3 text-center">Status</th>
                            <th scope="col" className="px-6 py-3 text-right">Akcje</th>
                        </tr>
                    </thead>
                    <tbody>
                        {expenses.map(expense => (
                            <tr key={expense.id} className="bg-white/50 dark:bg-gray-800/50 border-b dark:border-gray-700/50 hover:bg-cream-50 dark:hover:bg-gray-600/50">
                                <th scope="row" className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">{expense.name}</th>
                                <td className="px-6 py-4">{expense.cost.toLocaleString('pl-PL')} zł</td>
                                <td className="px-6 py-4 text-center">
                                    <button onClick={() => togglePaid(expense.id)}>
                                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${expense.paid ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'}`}>
                                          {expense.paid ? 'Zapłacone' : 'Do zapłaty'}
                                      </span>
                                    </button>
                                </td>
                                <td className="px-6 py-4 text-right"><button onClick={() => deleteExpense(expense.id)}><Trash2 size={16} className="text-rose-500 hover:text-rose-700"/></button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
// #endregion

// #region --- Vendors Manager ---
const emptyVendorForm = {
    name: '',
    category: 'venue',
    phone: '',
    email: '',
    totalCost: '',
    depositPaid: '',
    status: 'todo',
    notes: '',
};

const VendorsManager = ({ vendors, setVendors, showNotification }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingVendor, setEditingVendor] = useState(null);
    const [form, setForm] = useState(emptyVendorForm);

    const categoryLabels = {
        venue: 'Sala',
        catering: 'Catering',
        photo: 'Foto / Video',
        music: 'Muzyka',
        decor: 'Dekoracje',
        beauty: 'Beauty',
        transport: 'Transport',
        other: 'Inne',
    };

    const statusLabels = {
        todo: 'Do ustalenia',
        negotiating: 'W trakcie',
        confirmed: 'Potwierdzone',
        paid: 'Opłacone',
    };

    const openAddModal = () => {
        setEditingVendor(null);
        setForm(emptyVendorForm);
        setIsModalOpen(true);
    };

    const openEditModal = (vendor) => {
        setEditingVendor(vendor);
        setForm({
            name: vendor.name || '',
            category: vendor.category || 'venue',
            phone: vendor.phone || '',
            email: vendor.email || '',
            totalCost: vendor.totalCost ?? '',
            depositPaid: vendor.depositPaid ?? '',
            status: vendor.status || 'todo',
            notes: vendor.notes || '',
        });
        setIsModalOpen(true);
    };

    const saveVendor = (event) => {
        event.preventDefault();
        if (!form.name.trim()) {
            showNotification('Podaj nazwę dostawcy.', 'error');
            return;
        }

        const payload = {
            name: form.name.trim(),
            category: form.category,
            phone: form.phone.trim(),
            email: form.email.trim(),
            totalCost: Number(form.totalCost || 0),
            depositPaid: Number(form.depositPaid || 0),
            status: form.status,
            notes: form.notes.trim(),
        };

        if (editingVendor) {
            setVendors(prev => prev.map(v => v.id === editingVendor.id ? { ...v, ...payload } : v));
            showNotification('Dostawca został zaktualizowany.', 'success');
        } else {
            setVendors(prev => [...prev, { id: crypto.randomUUID(), ...payload }]);
            showNotification('Dodano nowego dostawcę.', 'success');
        }

        setIsModalOpen(false);
        setEditingVendor(null);
        setForm(emptyVendorForm);
    };

    const deleteVendor = (vendorId) => {
        const vendor = vendors.find(v => v.id === vendorId);
        if (!window.confirm(`Usunąć dostawcę: ${vendor?.name || 'ten wpis'}?`)) return;
        setVendors(prev => prev.filter(v => v.id !== vendorId));
        showNotification('Dostawca usunięty.', 'info');
    };

    const totalBudget = vendors.reduce((sum, vendor) => sum + (Number(vendor.totalCost) || 0), 0);
    const totalDeposit = vendors.reduce((sum, vendor) => sum + (Number(vendor.depositPaid) || 0), 0);

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <h2 className="text-3xl font-bold bg-gradient-to-r from-rose-600 to-purple-600 bg-clip-text text-transparent">
                    Dostawcy ({vendors.length})
                </h2>
                <button onClick={openAddModal} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-500 to-purple-500 text-white rounded-xl font-semibold shadow-lg hover:scale-105 transition-transform">
                    <Plus size={16} /> Dodaj dostawcę
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-800/60 p-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Łączny koszt</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{totalBudget.toLocaleString('pl-PL')} zł</p>
                </div>
                <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-800/60 p-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Zapłacone zaliczki</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{totalDeposit.toLocaleString('pl-PL')} zł</p>
                </div>
            </div>

            {vendors.length === 0 ? (
                <div className="text-center py-16 bg-white dark:bg-gray-800/40 backdrop-blur-xl rounded-xl">
                    <Briefcase size={48} className="mx-auto mb-4 text-gray-400" />
                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Brak dostawców</h3>
                    <p className="text-gray-500 dark:text-gray-400">Dodaj fotografa, salę, DJ-a i resztę usługodawców.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {vendors.map(vendor => (
                        <div key={vendor.id} className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/70 p-4 shadow-sm">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{vendor.name}</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">{categoryLabels[vendor.category] || 'Inne'}</p>
                                </div>
                                <span className="px-2 py-1 rounded-full text-xs font-semibold bg-cream-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                                    {statusLabels[vendor.status] || 'Do ustalenia'}
                                </span>
                            </div>
                            <div className="mt-3 space-y-1 text-sm text-gray-600 dark:text-gray-300">
                                {vendor.phone && <p className="flex items-center gap-2"><Phone size={14} /> {vendor.phone}</p>}
                                {vendor.email && <p className="flex items-center gap-2"><Mail size={14} /> {vendor.email}</p>}
                                <p>Koszt: <strong>{(Number(vendor.totalCost) || 0).toLocaleString('pl-PL')} zł</strong></p>
                                <p>Zaliczka: {(Number(vendor.depositPaid) || 0).toLocaleString('pl-PL')} zł</p>
                                {vendor.notes && <p className="text-xs text-gray-500 dark:text-gray-400 whitespace-pre-wrap">{vendor.notes}</p>}
                            </div>
                            <div className="mt-4 flex items-center gap-2">
                                <button onClick={() => openEditModal(vendor)} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">Edytuj</button>
                                <button onClick={() => deleteVendor(vendor.id)} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">Usuń</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {isModalOpen && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setIsModalOpen(false)}>
                    <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-2xl border border-gray-200 dark:border-gray-700 max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                            {editingVendor ? 'Edytuj dostawcę' : 'Dodaj dostawcę'}
                        </h3>
                        <form onSubmit={saveVendor} className="space-y-3">
                            <input value={form.name} onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))} placeholder="Nazwa dostawcy" className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-600" />
                            <select value={form.category} onChange={e => setForm(prev => ({ ...prev, category: e.target.value }))} className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-600">
                                {Object.entries(categoryLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                            </select>
                            <input value={form.phone} onChange={e => setForm(prev => ({ ...prev, phone: e.target.value }))} placeholder="Telefon" className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-600" />
                            <input value={form.email} onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))} placeholder="Email" className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-600" />
                            <div className="grid grid-cols-2 gap-3">
                                <input type="number" value={form.totalCost} onChange={e => setForm(prev => ({ ...prev, totalCost: e.target.value }))} placeholder="Całkowity koszt (zł)" className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-600" />
                                <input type="number" value={form.depositPaid} onChange={e => setForm(prev => ({ ...prev, depositPaid: e.target.value }))} placeholder="Zaliczka (zł)" className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-600" />
                            </div>
                            <select value={form.status} onChange={e => setForm(prev => ({ ...prev, status: e.target.value }))} className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-600">
                                {Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                            </select>
                            <textarea value={form.notes} onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))} rows={3} placeholder="Notatki (opcjonalnie)" className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-600" />
                            <div className="pt-2 flex items-center justify-end gap-2">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 font-semibold">Anuluj</button>
                                <button type="submit" className="px-4 py-2 rounded-lg bg-gradient-to-r from-rose-500 to-purple-500 text-white font-semibold">
                                    {editingVendor ? 'Zapisz zmiany' : 'Dodaj'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};
// #endregion

// #region --- GUEST & SEATING COMPONENTS ---

const GuestList = ({ guests, setGuests, showNotification, toggleGuestField, sortField, setSortField, sortDirection, setSortDirection }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isAddGuestModalOpen, setIsAddGuestModalOpen] = useState(false);
    const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
    const [linkingGuest, setLinkingGuest] = useState(null);

    const guestsToShow = useFilteredGuests(guests, 'all', searchTerm);

    const updateGuest = (guestId, updatedData) => {
        setGuests(prev => prev.map(g => g.id === guestId ? {...g, ...updatedData} : g))
    };

    const linkPartner = useCallback((guest1Id, guest2Id) => {
        setGuests(prev => prev.map(g => {
            if (g.id === guest1Id) return { ...g, partnerId: guest2Id };
            if (g.id === guest2Id) return { ...g, partnerId: guest1Id };
            return g;
        }));
        showNotification('Goście zostali połączeni w parę.', 'success');
    }, [setGuests, showNotification]);

    const unlinkPartner = useCallback((guestId) => {
        const guest = guests.find(g => g.id === guestId);
        if (!guest?.partnerId) return;
        const partnerId = guest.partnerId;
        setGuests(prev => prev.map(g => (
            g.id === guestId || g.id === partnerId
                ? { ...g, partnerId: null }
                : g
        )));
        showNotification('Połączenie pary zostało usunięte.', 'info');
    }, [guests, setGuests, showNotification]);

    return (
        <div className="space-y-6">
             <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                 <h2 className="text-3xl font-bold bg-gradient-to-r from-rose-600 to-purple-600 bg-clip-text text-transparent">Lista Gości ({guests.length})</h2>
                 <div className="flex items-center gap-2">
                     <div className="relative flex-1 md:flex-none">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={18} />
                        <input type="text" placeholder="Szukaj gości..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full md:w-64 pl-10 pr-4 py-2 bg-cream-100 dark:bg-gray-800 border border-transparent focus:border-rose-300 dark:focus:border-rose-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-500/40 text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 transition-all" />
                    </div>
                     <div className="p-1 bg-cream-200/80 dark:bg-gray-700/80 rounded-lg flex items-center">
                        <button onClick={() => setViewMode('table')} className={`p-1.5 rounded-md transition-all ${viewMode === 'table' ? 'bg-white dark:bg-gray-600 shadow-sm text-rose-500' : 'text-gray-500 hover:text-gray-800'}`} title="Widok tabeli"><List size={18} /></button>
                        <button onClick={() => setViewMode('cards')} className={`p-1.5 rounded-md transition-all ${viewMode === 'cards' ? 'bg-white dark:bg-gray-600 shadow-sm text-rose-500' : 'text-gray-500 hover:text-gray-800'}`} title="Widok kart"><LayoutGrid size={18} /></button>
                    </div>
                     <button onClick={() => setIsAddGuestModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-500 to-purple-500 text-white rounded-xl font-semibold transition-all hover:scale-105 shadow-lg active:scale-95"><Plus size={16} /> Dodaj</button>
                 </div>
            </div>
            
            {viewMode === 'table' ? (
                <GuestListTableView
                    guests={guestsToShow}
                    allGuests={guests}
                    setGuests={setGuests}
                    showNotification={showNotification}
                    toggleGuestField={toggleGuestField}
                    sortField={sortField}
                    setSortField={setSortField}
                    sortDirection={sortDirection}
                    setSortDirection={setSortDirection}
                    updateGuest={updateGuest}
                    setLinkingGuest={setLinkingGuest}
                    unlinkPartner={unlinkPartner}
                />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {guestsToShow.map(guest => (
                        <GuestDetailCard key={guest.id} guest={guest} updateGuest={updateGuest} toggleGuestField={toggleGuestField} setGuests={setGuests} />
                    ))}
                </div>
            )}


            {guestsToShow.length === 0 && (
                <div className="text-center py-16 bg-white dark:bg-gray-800/40 backdrop-blur-xl rounded-xl mt-4 col-span-full">
                    <Users size={48} className="mx-auto mb-4 text-gray-400" />
                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Brak gości</h3>
                    <p className="text-gray-500 dark:text-gray-400">Dodaj swojego pierwszego gościa, aby zacząć.</p>
                </div>
            )}
            
            <AddGuestModal
                isOpen={isAddGuestModalOpen}
                onClose={() => setIsAddGuestModalOpen(false)}
                setGuests={setGuests}
                showNotification={showNotification}
            />
            {linkingGuest && (
                <LinkPartnerModal
                    guest={linkingGuest}
                    allGuests={guests}
                    onClose={() => setLinkingGuest(null)}
                    onLinkPartner={linkPartner}
                />
            )}
        </div>
    );
};

const GuestListTableView = ({ guests, allGuests, setGuests, showNotification, toggleGuestField, sortField, setSortField, sortDirection, setSortDirection, updateGuest, setLinkingGuest, unlinkPartner }) => {
    const [selectedGuests, setSelectedGuests] = useState(new Set());
    const [editingCell, setEditingCell] = useState(null); // e.g. { guestId: 'x', field: 'children' }
    const [isTagEditorOpen, setIsTagEditorOpen] = useState(false);
    const [currentGuestForTags, setCurrentGuestForTags] = useState(null);
    
     const sortedGuests = useMemo(() => {
        return [...guests].sort((a, b) => {
            let aVal = a[sortField];
            let bVal = b[sortField];
            if (sortField === 'name') {
                aVal = `${a.firstName} ${a.lastName}`;
                bVal = `${b.firstName} ${b.lastName}`;
            }
            if (typeof aVal === 'string') {
                return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }
            if (typeof aVal === 'boolean' || aVal === null) {
                const valA = aVal === true ? 2 : aVal === false ? 1 : 0;
                const valB = bVal === true ? 2 : bVal === false ? 1 : 0;
                return sortDirection === 'asc' ? valA - valB : valB - valA;
            }
            if (aVal === null) aVal = -1;
            if (bVal === null) bVal = -1;

            if (sortDirection === 'asc') {
                return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
            } else {
                return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
            }
        });
    }, [guests, sortField, sortDirection]);
    
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };
    
    const selectAllGuests = () => {
        if (selectedGuests.size === sortedGuests.length && sortedGuests.length > 0) {
            setSelectedGuests(new Set());
        } else {
            setSelectedGuests(new Set(sortedGuests.map(g => g.id)));
        }
    };

    const deleteGuest = (id) => {
        setGuests(prev => {
            const guestToDelete = prev.find(g => g.id === id);
            const partnerId = guestToDelete?.partnerId || null;
            return prev
                .filter(g => g.id !== id)
                .map(g => g.id === partnerId ? { ...g, partnerId: null } : g);
        });
        setSelectedGuests(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });
        showNotification('Gość został usunięty!', 'info');
    };
    
    const openTagEditor = (guest) => {
        setCurrentGuestForTags(guest);
        setIsTagEditorOpen(true);
    };

    const handleTagSave = (newTags) => {
        updateGuest(currentGuestForTags.id, { tags: newTags });
        setIsTagEditorOpen(false);
    };

    return (
        <>
         <div className="overflow-hidden rounded-xl border border-cream-200/50 dark:border-gray-700/50 bg-white/70 dark:bg-gray-800/60 backdrop-blur-xl shadow-lg">
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-cream-50/50 dark:bg-gray-700/40 backdrop-blur-sm">
                        <tr>
                            <th className="p-3 text-center align-middle w-14">
                                <input type="checkbox" checked={selectedGuests.size === sortedGuests.length && sortedGuests.length > 0} onChange={selectAllGuests} className="w-4 h-4 text-rose-600 bg-white border-2 border-gray-300 rounded focus:ring-rose-500 focus:ring-2 transition-all" />
                            </th>
                            {[
                                { key: 'name', label: 'Gość' }, { key: 'tags', label: 'Tagi' }, { key: 'wedding', label: 'Wesele' }, { key: 'afterparty', label: 'Poprawiny' }, { key: 'accommodation', label: 'Nocleg' },
                                { key: 'children', label: 'Dzieci' }, { key: 'transport', label: 'Transport' }, { key: 'confirmed', label: 'Status' }, { key: 'actions', label: 'Akcje' }
                            ].map(column => (
                                <th
                                    key={column.key}
                                    className={`p-3 text-sm font-bold text-gray-700 dark:text-gray-200 align-middle ${
                                        ['wedding', 'afterparty', 'accommodation', 'children', 'transport', 'confirmed', 'actions'].includes(column.key) ? 'text-center' : 'text-left'
                                    } ${column.key !== 'actions' ? 'cursor-pointer hover:bg-cream-100/30 dark:hover:bg-gray-600/20 transition-colors rounded-lg' : ''}`}
                                    onClick={() => column.key !== 'actions' && handleSort(column.key)}
                                >
                                    <div className={`flex items-center gap-2 ${
                                        ['wedding', 'afterparty', 'accommodation', 'children', 'transport', 'confirmed', 'actions'].includes(column.key) ? 'justify-center' : ''
                                    }`}>
                                        {column.label}
                                        {column.key !== 'actions' && sortField === column.key && (<div className="text-rose-500">{sortDirection === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}</div>)}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-cream-200/50 dark:divide-gray-700/50">
                        {sortedGuests.map((guest) => (
                            <tr key={guest.id} className={`hover:bg-cream-50/20 dark:hover:bg-gray-700/10 transition-all duration-200 ${selectedGuests.has(guest.id) ? 'bg-rose-50/30 dark:bg-rose-900/10 ring-1 ring-rose-200/50 dark:ring-rose-800/50' : ''}`}>
                                <td className="p-3 text-center align-middle">
                                    <input type="checkbox" checked={selectedGuests.has(guest.id)} onChange={(e) => {
                                        const newSelected = new Set(selectedGuests);
                                        if (e.target.checked) newSelected.add(guest.id); else newSelected.delete(guest.id);
                                        setSelectedGuests(newSelected);
                                    }} className="w-4 h-4 text-rose-600 bg-white border-2 border-gray-300 rounded focus:ring-rose-500 focus:ring-2 transition-all" />
                                </td>
                                <td className="p-3 align-middle">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md ring-2 ring-white/20" style={{ backgroundColor: guest.color }}>{guest.firstName[0]}{guest.lastName[0]}</div>
                                        <div>
                                            <div className="font-semibold text-gray-900 dark:text-gray-100 text-sm">{guest.firstName} {guest.lastName}</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                {guest.partnerId
                                                    ? `w parze z ${allGuests.find(g => g.id === guest.partnerId)?.firstName || ''}`
                                                    : (guest.email || 'brak email')}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td className="p-3 cursor-pointer align-middle" onClick={() => openTagEditor(guest)}>
                                    <div className="flex flex-wrap gap-1 max-w-28">
                                        {guest.tags.slice(0, 2).map((tag, i) => (<span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-100/60 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs font-medium"><Tag size={8} />{tag}</span>))}
                                        {guest.tags.length > 2 && <span className="text-xs text-gray-500">+{guest.tags.length - 2}</span>}
                                        {guest.tags.length === 0 && <span className="text-xs text-gray-400 italic">Brak</span>}

                                    </div>
                                </td>
                                <td className="p-3 text-center align-middle">{renderStatusIcon(guest.wedding, guest.id, 'wedding', toggleGuestField)}</td>
                                <td className="p-3 text-center align-middle">{renderStatusIcon(guest.afterparty, guest.id, 'afterparty', toggleGuestField)}</td>
                                <td className="p-3 text-center align-middle">{renderStatusIcon(guest.accommodation, guest.id, 'accommodation', toggleGuestField, false)}</td>
                                <td className="p-3 text-center align-middle">
                                    <div className="flex items-center justify-center gap-1">
                                        <button onClick={() => updateGuest(guest.id, { children: Math.max(0, guest.children - 1)})} className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-cream-200 dark:hover:bg-gray-600"><Minus size={12}/></button>
                                        <span className="text-sm font-bold w-4">{guest.children}</span>
                                        <button onClick={() => updateGuest(guest.id, { children: guest.children + 1})} className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-cream-200 dark:hover:bg-gray-600"><Plus size={12}/></button>
                                    </div>
                                </td>
                                 <td className="p-3 text-center align-middle">{renderStatusIcon(guest.transport, guest.id, 'transport', toggleGuestField, false)}</td>
                                <td className="p-3 text-center align-middle">{renderStatusIcon(guest.confirmed, guest.id, 'confirmed', toggleGuestField)}</td>
                                <td className="p-3 text-center align-middle">
                                    <div className="flex items-center justify-center gap-1">
                                        {guest.partnerId ? (
                                            <button
                                                onClick={() => unlinkPartner(guest.id)}
                                                className="w-7 h-7 flex items-center justify-center text-amber-600 dark:text-amber-400 hover:bg-amber-100/60 dark:hover:bg-amber-900/30 rounded-lg transition-all"
                                                title={`Rozłącz z ${allGuests.find(g => g.id === guest.partnerId)?.firstName || 'partnerem'}`}
                                            >
                                                <Unlink size={12} />
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => setLinkingGuest(guest)}
                                                className="w-7 h-7 flex items-center justify-center text-blue-600 dark:text-blue-400 hover:bg-blue-100/60 dark:hover:bg-blue-900/30 rounded-lg transition-all"
                                                title="Połącz w parę"
                                            >
                                                <Link2 size={12} />
                                            </button>
                                        )}
                                        <button onClick={() => deleteGuest(guest.id)} className="w-7 h-7 flex items-center justify-center text-red-600 dark:text-red-400 hover:bg-red-100/60 dark:hover:bg-red-900/30 rounded-lg transition-all" title="Usuń"><Trash2 size={12} /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
        {isTagEditorOpen && currentGuestForTags && (
             <TagEditorModal 
                guestName={`${currentGuestForTags.firstName} ${currentGuestForTags.lastName}`}
                tags={currentGuestForTags.tags} 
                onSave={handleTagSave} 
                onClose={() => setIsTagEditorOpen(false)} 
            />
        )}
        </>
    );
};

const GuestDetailCard = ({ guest, updateGuest, toggleGuestField, setGuests }) => {
    const [isEditing, setIsEditing] = useState(null);
    const [editValue, setEditValue] = useState("");
    const inputRef = useRef(null);
    const [isEditingTags, setIsEditingTags] = useState(false);


    const handleEdit = (field, currentValue) => {
        setIsEditing(field);
        setEditValue(currentValue);
    }

    const handleSave = () => {
        if (isEditing) {
            updateGuest(guest.id, { [isEditing]: editValue });
            setIsEditing(null);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSave();
        else if (e.key === 'Escape') setIsEditing(null);
    }
    
    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);
    
    const handleTagChange = (newTags) => {
        updateGuest(guest.id, { tags: newTags });
    };

    const renderEditableField = (field, value, type="text") => {
        if (isEditing === field) {
            return (
                <input
                    ref={inputRef}
                    type={type}
                    value={editValue}
                    onChange={(e) => setEditValue(type === 'number' ? parseInt(e.target.value) || 0 : e.target.value)}
                    onBlur={handleSave}
                    onKeyDown={handleKeyDown}
                    className="w-full bg-cream-200/50 dark:bg-gray-900/50 p-1 rounded-md text-sm outline-none ring-2 ring-rose-500"
                />
            );
        }
        return <span className="text-gray-800 dark:text-gray-200 font-semibold cursor-pointer" onClick={() => handleEdit(field, value)}>{value}</span>;
    };
    
    return (
        <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl border border-cream-200/50 dark:border-gray-700/50 shadow-lg p-4 flex flex-col gap-4 transition-all hover:shadow-2xl hover:scale-[1.02]">
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center text-white text-lg font-bold shadow-lg" style={{ backgroundColor: guest.color }}>{guest.firstName[0]}{guest.lastName[0]}</div>
                    <div>
                        <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100">{guest.firstName} {guest.lastName}</h3>
                        <p className="text-xs text-purple-600 dark:text-purple-400">{guest.plusOne ? `+ ${guest.plusOne}` : 'bez os. tow.'}</p>
                    </div>
                </div>
                <button
                    onClick={() => setGuests(prev => {
                        const partnerId = prev.find(g => g.id === guest.id)?.partnerId || null;
                        return prev
                            .filter(g => g.id !== guest.id)
                            .map(g => g.id === partnerId ? { ...g, partnerId: null } : g);
                    })}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                >
                    <Trash2 size={16} />
                </button>
            </div>
            
             <div className="flex flex-wrap gap-1 cursor-pointer" onClick={() => setIsEditingTags(true)}>
                 {guest.tags.map(tag => <span key={tag} className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">{tag}</span>)}
                 {guest.tags.length === 0 && <span className="text-xs text-gray-400 italic">Brak tagów</span>}
            </div>

            <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="flex flex-col items-center gap-1 p-2 rounded-lg bg-cream-100 dark:bg-gray-700/50">
                    <span className="text-gray-500">Wesele</span>
                    {renderStatusIcon(guest.wedding, guest.id, 'wedding', toggleGuestField)}
                </div>
                 <div className="flex flex-col items-center gap-1 p-2 rounded-lg bg-cream-100 dark:bg-gray-700/50">
                    <span className="text-gray-500">Poprawiny</span>
                    {renderStatusIcon(guest.afterparty, guest.id, 'afterparty', toggleGuestField)}
                </div>
                 <div className="flex flex-col items-center gap-1 p-2 rounded-lg bg-cream-100 dark:bg-gray-700/50">
                    <span className="text-gray-500">Potwierdz.</span>
                    {renderStatusIcon(guest.confirmed, guest.id, 'confirmed', toggleGuestField)}
                </div>
            </div>
            
            <div className="text-sm space-y-2 text-gray-500 dark:text-gray-400">
                <div className="flex justify-between items-center"><span className="flex items-center gap-2"><Baby size={14}/> Dzieci:</span> {renderEditableField('children', guest.children, 'number')}</div>
                <div className="flex justify-between items-center"><span className="flex items-center gap-2"><Hotel size={14}/> Nocleg:</span> {renderStatusIcon(guest.accommodation, guest.id, 'accommodation', toggleGuestField, false)}</div>
                <div className="flex justify-between items-center"><span className="flex items-center gap-2"><Car size={14}/> Transport:</span> {renderStatusIcon(guest.transport, guest.id, 'transport', toggleGuestField, false)}</div>
            </div>
             {isEditingTags && (
                <TagEditorModal 
                    guestName={`${guest.firstName} ${guest.lastName}`}
                    tags={guest.tags} 
                    onSave={handleTagChange} 
                    onClose={() => setIsEditingTags(false)} 
                />
            )}
        </div>
	    )
	}

const LinkPartnerModal = ({ guest, allGuests, onClose, onLinkPartner }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const potentialPartners = useMemo(() => {
        return allGuests.filter((person) =>
            !person.partnerId &&
            person.id !== guest.id &&
            (`${person.firstName} ${person.lastName}`).toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [allGuests, guest.id, searchTerm]);

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/75" onClick={onClose}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-2xl border border-gray-300 dark:border-gray-700 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()}>
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Połącz w parę</h3>
                <p className="text-rose-500 font-semibold mb-4">{guest.firstName} {guest.lastName}</p>
                <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                    <input
                        type="text"
                        placeholder="Szukaj partnera..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full pl-10 p-2 bg-white dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600"
                    />
                </div>
                <div className="max-h-60 overflow-y-auto space-y-1 pr-1">
                    {potentialPartners.length > 0 ? potentialPartners.map(person => (
                        <button
                            key={person.id}
                            onClick={() => { onLinkPartner(guest.id, person.id); onClose(); }}
                            className="w-full flex items-center gap-3 p-2 text-left hover:bg-cream-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                            <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: person.color }}>
                                {person.firstName[0]}{person.lastName[0]}
                            </div>
                            <span className="font-medium">{person.firstName} {person.lastName}</span>
                        </button>
                    )) : (
                        <p className="text-center text-sm text-gray-500 py-4">Brak dostępnych gości do połączenia.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

const TagEditorModal = ({ guestName, tags, onSave, onClose }) => {
    const [currentTags, setCurrentTags] = useState(tags);
    const [newTag, setNewTag] = useState("");

    const addTag = () => {
        if (newTag && !currentTags.includes(newTag)) {
            setCurrentTags([...currentTags, newTag]);
            setNewTag("");
        }
    };

    const removeTag = (tagToRemove) => {
        setCurrentTags(currentTags.filter(tag => tag !== tagToRemove));
    };

    const handleSave = () => {
        onSave(currentTags);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-2xl border border-gray-200 dark:border-gray-700 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()}>
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Edytuj tagi dla</h3>
                <p className="text-rose-500 font-semibold mb-4">{guestName}</p>
                <div className="space-y-3">
                    <div className="flex items-center gap-2">
                        <input 
                            type="text" 
                            value={newTag}
                            onChange={(e) => setNewTag(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && addTag()}
                            placeholder="Dodaj nowy tag..."
                            className="flex-1 p-2.5 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg border border-gray-300 dark:border-gray-600 placeholder:text-gray-400"
                        />
                        <button onClick={addTag} className="p-2 bg-rose-500 text-white rounded-lg"><Plus size={16}/></button>
                    </div>
                    <div className="flex flex-wrap gap-2 p-2 min-h-[4rem] bg-gray-50 dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-700">
                        {currentTags.length === 0 && <span className="text-sm text-gray-500 p-2">Brak tagów</span>}
                        {currentTags.map(tag => (
                            <div key={tag} className="flex items-center gap-1.5 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded-full px-2 py-1 text-sm font-medium">
                                <span>{tag}</span>
                                <button onClick={() => removeTag(tag)} className="text-blue-500 hover:text-blue-700 dark:hover:text-blue-300"><X size={12}/></button>
                            </div>
                        ))}
                    </div>
                </div>
                 <button onClick={handleSave} className="w-full mt-6 flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-500 to-purple-500 text-white font-semibold rounded-lg shadow-lg hover:scale-105 transition-transform">
                    <Save size={16} /> Zapisz zmiany
                </button>
            </div>
        </div>
    )
}

const renderStatusIcon = (value, guestId, field, toggleGuestField, cycle = true) => {
    const baseClasses = 'p-1.5 rounded-full transition-all duration-200 hover:scale-110 active:scale-95';
    let icon, classes;
    
    const handleClick = (e) => {
        e.stopPropagation();
        toggleGuestField(guestId, field)
    };

    if (cycle) { // For statuses: confirmed, wedding, afterparty
        if (value === true) {
            icon = <CheckCircle size={18} />;
            classes = `${baseClasses} text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30`;
        } else if (value === false) {
            icon = <X size={18} />;
            classes = `${baseClasses} text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30`;
        } else { // null
            icon = <Minus size={18} />;
            classes = `${baseClasses} text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30`;
        }
    } else { // For binary toggles: accommodation, transport
         if (value) {
            icon = <CheckCircle size={18} />;
            classes = `${baseClasses} text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30`;
        } else {
             icon = <Circle size={18} />;
             classes = `${baseClasses} text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700/50`;
        }
    }
    
    return <button onClick={handleClick} className={classes}>{icon}</button>;
};

// Seating Plan and related components
const SeatingPlan = ({
    guests, tables, decorations,
    selectedItem, setSelectedItem,
    showSeatPicker, setShowSeatPicker,
    selectedSeat, setSelectedSeat,
    showAddMenu, setShowAddMenu,
    guestListView, setGuestListView,
    isAiSeatingLoading, setIsAiSeatingLoading,
    darkMode, showGrid, setShowGrid,
    showNotification,
    assignGuestToSeat, removeGuestFromSeat,
    addTable, addDecoration,
    setTables, setDecorations, setGuests,
    decorationTypes,
    inspectorState, setInspectorState
}) => {
    const svgRef = useRef(null);
    const itemRefs = useRef({});
    const [viewTransform, setViewTransform] = useState({ x: 0, y: 0, k: 0.7 });
    const interactionState = useRef({ type: 'none' });
    const [spacebarPressed, setSpacebarPressed] = useState(false);
    const [snapLines, setSnapLines] = useState([]);
    const [hoveredTableId, setHoveredTableId] = useState(null);
    const [showResetLayoutConfirm, setShowResetLayoutConfirm] = useState(false);

    const undo = useCallback(() => { showNotification('Funkcja Cofnij nie jest jeszcze zaimplementowana.', 'info'); }, [showNotification]);
    const redo = useCallback(() => { showNotification('Funkcja Ponów nie jest jeszcze zaimplementowana.', 'info'); }, [showNotification]);

    const handleResetLayout = useCallback(() => {
        const defaultLayout = createEmptyAppState();
        setTables(defaultLayout.tables);
        setDecorations(defaultLayout.decorations);
        setGuests(prevGuests =>
            prevGuests.map(guest => ({ ...guest, tableId: null, seatNumber: null, tableAssigned: false }))
        );
        setSelectedItem(null);
        setShowSeatPicker(false);
        setShowAddMenu(null);
        setShowResetLayoutConfirm(false);
        setGuestListView('unassigned');
        setViewTransform({ x: 0, y: 0, k: 0.7 });
        showNotification('Układ sali został zresetowany.', 'info');
    }, [setDecorations, setGuests, setGuestListView, setSelectedItem, setShowAddMenu, setShowSeatPicker, setTables, showNotification]);

    useEffect(() => {
        if (selectedItem) {
            const itemExists = selectedItem.type === 'table'
                ? tables.some(t => t.id === selectedItem.id)
                : decorations.some(d => d.id === selectedItem.id);

            if (!itemExists) {
                setSelectedItem(null);
            }
        }
    }, [selectedItem, tables, decorations, setSelectedItem]);

    const handleAiSeatAssign = async () => {
        setIsAiSeatingLoading(true);
        showNotification('✨ Myślę nad najlepszym układem stołów...', 'info', 10000);

        const availableSeats = tables.map(table => {
            const assignedGuestsCount = guests.filter(g => g.tableId === table.id).length;
            return { tableId: table.id, tableName: table.name, availableSeats: table.seats - assignedGuestsCount };
        }).filter(t => t.availableSeats > 0);

        const unassignedGuestsToSeat = guests.filter(g => !g.tableId && g.wedding).map(g => ({
            guestId: g.id, name: `${g.firstName} ${g.lastName}`, tags: g.tags, plusOne: g.plusOne,
        }));

        const prompt = `Jesteś ekspertem od planowania wesel. Twoim zadaniem jest rozsadzenie gości przy stołach w sposób logiczny i harmonijny. Oto dostępne stoły i miejsca: ${JSON.stringify(availableSeats, null, 2)} Oto goście do rozsadzenia: ${JSON.stringify(unassignedGuestsToSeat, null, 2)} Zasady: 1. Grupuj gości z podobnymi tagami (np. 'rodzinaPM' z 'rodzinaPM', 'przyjaciele' z 'przyjaciele'). 2. Goście z tagiem 'vip', 'świadek', 'druhna' powinni siedzieć przy ważnych stołach. 3. Jeśli gość ma osobę towarzyszącą (plusOne), usadź ich razem przy tym samym stole. 4. Zwróć odpowiedź WYŁĄCZNIE w formacie JSON, bez żadnego dodatkowego tekstu.`;

        try {
            const apiKey = ""; // Pusty klucz dla Canvas
            if (apiKey === "YOUR_GEMINI_API_KEY") {
                 showNotification('Klucz API do Gemini nie został ustawiony!', 'error', 10000);
                 setIsAiSeatingLoading(false);
                 return;
            }
            
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${apiKey}`;
            const payload = {
                contents: [{ role: "user", parts: [{ text: prompt }] }],
                generationConfig: { responseMimeType: "application/json" }
            };

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error(`Błąd API: ${response.statusText}`);

            const result = await response.json();
            if (result.candidates && result.candidates.length > 0) {
                const textResponse = result.candidates[0].content.parts[0].text;
                const cleanedText = textResponse.replace(/^```json\s*|```\s*$/g, '');
                const aiResponse = JSON.parse(cleanedText);
                const assignments = aiResponse.assignments;
                let newGuestsState = [...guests];
                const seatCounters = {};

                assignments.forEach(assignment => {
                    const guestIndex = newGuestsState.findIndex(g => String(g.id) === String(assignment.guestId));
                    if (guestIndex > -1) {
                        if (!seatCounters[assignment.tableId]) {
                            const existingGuests = newGuestsState.filter(g => String(g.tableId) === String(assignment.tableId)).length;
                            seatCounters[assignment.tableId] = existingGuests + 1;
                        } else {
                            seatCounters[assignment.tableId]++;
                        }
                        const table = tables.find(t => String(t.id) === String(assignment.tableId));
                        if (table && seatCounters[assignment.tableId] <= table.seats) {
                            newGuestsState[guestIndex] = { ...newGuestsState[guestIndex], tableId: assignment.tableId, seatNumber: seatCounters[assignment.tableId], tableAssigned: true };
                        }
                    }
                });
                setGuests(newGuestsState);
                showNotification('✨ Goście zostali inteligentnie rozsadzeni!', 'success');
            } else {
                throw new Error("AI nie zwróciło propozycji.");
            }
        } catch (error) {
            console.error("Błąd AI:", error);
            showNotification(`Wystąpił błąd AI: ${error.message}`, 'error');
        } finally {
            setIsAiSeatingLoading(false);
        }
    };

    const getSVGPoint = useCallback((screenX, screenY) => {
        if (!svgRef.current) return { x: 0, y: 0 };
        const svg = svgRef.current;
        const pt = svg.createSVGPoint();
        pt.x = screenX;
        pt.y = screenY;
        const svgPoint = pt.matrixTransform(svg.getScreenCTM().inverse());
        return { x: svgPoint.x, y: svgPoint.y };
    }, []);

    const handleWheel = useCallback((e) => {
        e.preventDefault();
        const { x, y } = getSVGPoint(e.clientX, e.clientY);
        const zoomFactor = 1.1;
        setViewTransform(prev => {
            const newScale = e.deltaY < 0 ? prev.k * zoomFactor : prev.k / zoomFactor;
            const clampedScale = Math.max(0.1, Math.min(3, newScale));
            const newX = x - (x - prev.x) * (clampedScale / prev.k);
            const newY = y - (y - prev.y) * (clampedScale / prev.k);
            return { x: newX, y: newY, k: clampedScale };
        });
    }, [getSVGPoint]);

    const handleCanvasMouseDown = useCallback((e) => {
        if (e.button !== 0 || e.target.closest('[data-item-id]')) return;
        interactionState.current = {
            type: 'panning',
            startX: e.clientX,
            startY: e.clientY,
            initialTransform: viewTransform
        };
        if (!inspectorState.isPinned) {
            setSelectedItem(null);
        }
    }, [viewTransform, setSelectedItem, inspectorState.isPinned]);

    const handleItemMouseDown = useCallback((e, item, itemType, handleType = 'drag') => {
        e.stopPropagation();
        if (e.button !== 0) return;
        const currentItem = itemType === 'table' ? tables.find(t => t.id === item.id) : decorations.find(d => d.id === item.id);
        if (currentItem?.isLocked) {
            interactionState.current = { type: 'select-locked', itemType, id: item.id };
            setSelectedItem({ type: itemType, id: item.id });
            return;
        }
        if (handleType === 'rotate') {
            interactionState.current = { type: 'rotating', id: item.id, itemType, centerX: item.x, centerY: item.y };
        } else if (handleType.startsWith('resize')) {
            interactionState.current = { type: 'resizing', id: item.id, itemType, handle: handleType, startX: e.clientX, startY: e.clientY, initialItem: { ...item } };
        } else {
            interactionState.current = { type: 'potential-drag', id: item.id, itemType, startX: e.clientX, startY: e.clientY, initialItemX: item.x, initialItemY: item.y, moved: false };
        }
    }, [tables, decorations, setSelectedItem]);

    const checkForSnap = useCallback((movingItem, otherItems) => {
        const snapDistance = 10 / viewTransform.k;
        let newLines = [];
        let snapX = null;
        let snapY = null;

        const getBounds = (item) => ({
            left: item.x - (item.width ? item.width / 2 : (item.radius || 0)),
            right: item.x + (item.width ? item.width / 2 : (item.radius || 0)),
            top: item.y - (item.height ? item.height / 2 : (item.radius || 0)),
            bottom: item.y + (item.height ? item.height / 2 : (item.radius || 0)),
            centerX: item.x,
            centerY: item.y,
        });

        const movingBounds = getBounds(movingItem);

        for (const other of otherItems) {
            if (other.id === movingItem.id) continue;
            const otherBounds = getBounds(other);
            if (Math.abs(movingBounds.centerY - otherBounds.centerY) < snapDistance) { snapY = otherBounds.centerY; newLines.push({ type: 'h', pos: otherBounds.centerY }); }
            if (Math.abs(movingBounds.centerX - otherBounds.centerX) < snapDistance) { snapX = otherBounds.centerX; newLines.push({ type: 'v', pos: otherBounds.centerX }); }
        }
        setSnapLines(newLines);
        return { snapX, snapY };
    }, [viewTransform.k]);

    const handleMouseMove = useCallback((e) => {
        const state = interactionState.current;
        if (state.type === 'none') return;
        const mousePos = getSVGPoint(e.clientX, e.clientY);
        requestAnimationFrame(() => {
            const currentState = interactionState.current;
            if (currentState.type === 'none' || currentState.type === 'select-locked') return;

            if (currentState.type === 'potential-drag') {
                const dx = e.clientX - currentState.startX;
                const dy = e.clientY - currentState.startY;
                if (Math.sqrt(dx * dx + dy * dy) > 5) {
                    interactionState.current.type = 'dragging';
                    if (!inspectorState.isPinned) setSelectedItem(null);
                }
            } else if (currentState.type === 'dragging') {
                const dx = (e.clientX - currentState.startX) / viewTransform.k;
                const dy = (e.clientY - currentState.startY) / viewTransform.k;
                let newX = currentState.initialItemX + dx;
                let newY = currentState.initialItemY + dy;
                const currentItemData = currentState.itemType === 'table' ? tables.find(t => t.id === currentState.id) : decorations.find(d => d.id === currentState.id);
                const { snapX, snapY } = checkForSnap({ ...currentItemData, x: newX, y: newY }, [...tables, ...decorations]);
                if (snapX !== null) newX = snapX;
                if (snapY !== null) newY = snapY;
                interactionState.current.finalItem = { ...currentItemData, x: newX, y: newY };
                itemRefs.current[currentState.id]?.setAttribute('transform', `translate(${newX} ${newY}) rotate(${currentItemData.rotation || 0})`);
            } else if (currentState.type === 'panning') {
                const dx = e.clientX - currentState.startX;
                const dy = e.clientY - currentState.startY;
                setViewTransform({ ...currentState.initialTransform, x: currentState.initialTransform.x + dx, y: currentState.initialTransform.y + dy });
            }
             else if (currentState.type === 'rotating') {
                const itemRef = itemRefs.current[currentState.id];
                if (!itemRef) return;
                const angle = Math.atan2(mousePos.y - currentState.centerY, mousePos.x - currentState.centerX) * 180 / Math.PI;
                const newRotation = angle + 90; // Adjust for initial handle position

                const currentItemData = currentState.itemType === 'table' ? tables.find(t => t.id === currentState.id) : decorations.find(d => d.id === currentState.id);
                interactionState.current.finalItem = { ...currentItemData, rotation: newRotation };
                itemRef.setAttribute('transform', `translate(${currentItemData.x} ${currentItemData.y}) rotate(${newRotation})`);

            } else if (currentState.type === 'resizing') {
                const itemRef = itemRefs.current[currentState.id];
                if (!itemRef) return;
                const dx = (e.clientX - currentState.startX) / viewTransform.k;
                const dy = (e.clientY - currentState.startY) / viewTransform.k;
                const { initialItem, handle } = currentState;
                let { x, y, width, height, rotation } = initialItem;

                const rad = (rotation || 0) * Math.PI / 180;
                const cos = Math.cos(rad);
                const sin = Math.sin(rad);

                const rotatedDx = dx * cos + dy * sin;
                const rotatedDy = -dx * sin + dy * cos;

                let newX = x, newY = y, newWidth = width, newHeight = height;

                if (handle.includes('l')) { newWidth -= rotatedDx; newX += rotatedDx / 2 * cos; newY += rotatedDx / 2 * sin; }
                if (handle.includes('r')) { newWidth += rotatedDx; newX += rotatedDx / 2 * cos; newY += rotatedDx / 2 * sin; }
                if (handle.includes('t')) { newHeight -= rotatedDy; newX -= rotatedDy / 2 * sin; newY += rotatedDy / 2 * cos; }
                if (handle.includes('b')) { newHeight += rotatedDy; newX -= rotatedDy / 2 * sin; newY += rotatedDy / 2 * cos; }
                
                if (newWidth > 20 && newHeight > 20) {
                    interactionState.current.finalItem = { ...initialItem, x: newX, y: newY, width: newWidth, height: newHeight };
                    itemRef.setAttribute('transform', `translate(${newX} ${newY}) rotate(${rotation || 0})`);
                    const rect = itemRef.querySelector('rect');
                    if (rect) {
                        rect.setAttribute('x', -newWidth / 2); rect.setAttribute('y', -newHeight / 2);
                        rect.setAttribute('width', newWidth); rect.setAttribute('height', newHeight);
                    }
                }
            }
        });
    }, [viewTransform.k, tables, decorations, checkForSnap, setSelectedItem, viewTransform.x, viewTransform.y, getSVGPoint, inspectorState.isPinned]);

    const handleMouseUp = useCallback(() => {
        const state = interactionState.current;
        if ((state.type === 'potential-drag' && !state.moved) || state.type === 'select-locked') {
            setSelectedItem({ type: state.itemType, id: state.id });
        } else if (state.type === 'dragging' || state.type === 'rotating' || state.type === 'resizing') {
            if (state.finalItem) {
                const { id, itemType } = state;
                const stateUpdater = itemType === 'table' ? setTables : setDecorations;
                stateUpdater(prevItems => prevItems.map(item => item.id === id ? { ...item, ...state.finalItem } : item));
            }
        }
        interactionState.current = { type: 'none' };
        setSnapLines([]);
    }, [setSelectedItem, setTables, setDecorations]);

    const handleDoubleClick = useCallback((e) => {
        if (e.target.closest('[data-item-id]')) return;
        const { x, y } = getSVGPoint(e.clientX, e.clientY);
        setViewTransform(prev => ({ x: x - (x - prev.x) * 1.5, y: y - (y - prev.y) * 1.5, k: Math.min(prev.k * 1.5, 3) }));
    }, [getSVGPoint]);

    useEffect(() => {
        const svgEl = svgRef.current;
        if (!svgEl) return;
        const handleKeyDown = (e) => {
            if (e.code === 'Space' && !spacebarPressed) { setSpacebarPressed(true); }
            if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
            if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) { e.preventDefault(); redo(); }
        };
        const handleKeyUp = (e) => { if (e.code === 'Space') { setSpacebarPressed(false); } };

        svgEl.addEventListener('wheel', handleWheel, { passive: false });
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('mouseleave', handleMouseUp);

        return () => {
            svgEl.removeEventListener('wheel', handleWheel);
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('mouseleave', handleMouseUp);
        };
    }, [spacebarPressed, undo, redo, handleMouseMove, handleMouseUp, handleWheel]);
    
    return (
        <div className="relative flex h-[calc(100vh-220px)] gap-6">
            <GuestListSidebar
                guests={guests} tables={tables} guestListView={guestListView} setGuestListView={setGuestListView}
                handleAiSeatAssign={handleAiSeatAssign} isAiSeatingLoading={isAiSeatingLoading}
                selectedItem={selectedItem}
                hoveredTableId={hoveredTableId} setHoveredTableId={setHoveredTableId}
                setSelectedItem={setSelectedItem}
                removeGuestFromSeat={removeGuestFromSeat}
            />
            <div className="flex-1 relative rounded-2xl overflow-hidden bg-cream-100 dark:bg-slate-800/50 border border-gray-200/50 dark:border-gray-800/50 shadow-inner"
                 style={{ cursor: spacebarPressed || interactionState.current.type === 'panning' ? 'grabbing' : 'default' }}>
                <svg ref={svgRef} width="100%" height="100%" className="select-none transition-colors duration-300" onMouseDown={handleCanvasMouseDown} onDoubleClick={handleDoubleClick}>
                    <defs>
                        <pattern id="gridPattern" width="20" height="20" patternUnits="userSpaceOnUse" patternTransform={`scale(${viewTransform.k})`}>
                            <circle cx="10" cy="10" r="0.5" className={darkMode ? "fill-gray-700" : "fill-gray-300"} opacity={showGrid ? "0.3" : "0"} />
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill={showGrid ? "url(#gridPattern)" : (darkMode ? "#1e293b" : "#f1f5f9")} />
                    <g transform={`translate(${viewTransform.x} ${viewTransform.y}) scale(${viewTransform.k})`}>
                        {decorations.map(decoration =>
                            <DecorationItem
                                key={decoration.id} decoration={decoration}
                                isSelected={selectedItem?.type === 'decoration' && selectedItem?.id === decoration.id}
                                onMouseDown={handleItemMouseDown}
                                itemRef={el => itemRefs.current[decoration.id] = el}
                                darkMode={darkMode} decorationTypes={decorationTypes}
                                interactionState={interactionState.current}
                            />
                        )}
                        {tables.map(table =>
                            <TableItem
                                key={table.id} table={table} guests={guests}
                                isSelected={selectedItem?.type === 'table' && selectedItem?.id === table.id}
                                isHovered={hoveredTableId === table.id}
                                onMouseDown={handleItemMouseDown}
                                itemRef={el => itemRefs.current[table.id] = el}
                                darkMode={darkMode} interactionState={interactionState.current}
                                assignGuestToSeat={assignGuestToSeat} removeGuestFromSeat={removeGuestFromSeat}
                                setShowSeatPicker={setShowSeatPicker} setSelectedSeat={setSelectedSeat}
                                showNotification={showNotification} setHoveredTableId={setHoveredTableId}
                            />
                        )}
                        {snapLines.map((line, i) => (line.type === 'v' ? <line key={i} x1={line.pos} y1={-10000} x2={line.pos} y2={10000} stroke="#f472b6" strokeWidth={1 / viewTransform.k} strokeDasharray={`${4 / viewTransform.k}`} /> : <line key={i} x1={-10000} y1={line.pos} x2={10000} y2={line.pos} stroke="#f472b6" strokeWidth={1 / viewTransform.k} strokeDasharray={`${4 / viewTransform.k}`} />))}
                    </g>
                </svg>
                <div className="absolute top-4 right-4 flex items-center gap-2">
                     <div className="flex items-center gap-1 bg-white/80 dark:bg-gray-800/60 rounded-xl p-1 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 shadow-md">
                        <button onClick={() => setViewTransform(p => ({ ...p, k: Math.max(0.1, p.k / 1.2) }))} className="p-2 text-gray-600 dark:text-gray-300 hover:bg-cream-100 dark:hover:bg-gray-700 rounded-lg transition-all"><ZoomOut size={16} /></button>
                        <div className="px-3 text-sm text-gray-600 dark:text-gray-300 font-semibold">{Math.round(viewTransform.k * 100)}%</div>
                        <button onClick={() => setViewTransform(p => ({ ...p, k: Math.min(3, p.k * 1.2) }))} className="p-2 text-gray-600 dark:text-gray-300 hover:bg-cream-100 dark:hover:bg-gray-700 rounded-lg transition-all"><ZoomIn size={16} /></button>
                        <button onClick={() => setViewTransform({ x: 0, y: 0, k: 0.7 })} className="p-2 text-gray-600 dark:text-gray-300 hover:bg-cream-100 dark:hover:bg-gray-700 rounded-lg transition-all"><Maximize size={16} /></button>
                    </div>
                    <button onClick={() => setShowGrid(!showGrid)} className={`p-2 rounded-lg transition-all bg-white/80 dark:bg-gray-800/60 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 shadow-md ${showGrid ? 'text-rose-500' : 'text-gray-600 dark:text-gray-300'}`} title="Siatka"><Grid3x3 size={16} /></button>
                    <button onClick={() => setShowResetLayoutConfirm(true)} className="p-2 rounded-lg transition-all bg-white/80 dark:bg-gray-800/60 backdrop-blur-sm border border-red-200/60 dark:border-red-700/60 text-red-600 dark:text-red-400 shadow-md hover:bg-red-50 dark:hover:bg-red-900/20" title="Resetuj salę"><RotateCcw size={16} /></button>
                    <button onClick={() => setShowAddMenu(showAddMenu ? null : 'main')} className="p-2 rounded-lg bg-gradient-to-r from-rose-500 to-purple-500 text-white shadow-lg hover:scale-105 transition-transform"><Plus size={16} /></button>
                </div>
                 {showAddMenu === 'main' && (
                    <div className="absolute top-16 right-4 bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-2 space-y-1 z-10 border dark:border-gray-700">
                        <button onClick={() => { addTable('circle'); setShowAddMenu(null); }} className="w-full text-left flex items-center gap-2 p-2 hover:bg-cream-100 dark:hover:bg-gray-700 rounded-lg"><Table size={16} className="text-blue-500" /> Nowy Stół</button>
                        <button onClick={() => setShowAddMenu('decoration')} className="w-full text-left flex items-center gap-2 p-2 hover:bg-cream-100 dark:hover:bg-gray-700 rounded-lg"><Sparkles size={16} className="text-purple-500" /> Nowa Dekoracja</button>
                    </div>
                )}
                 {showAddMenu === 'decoration' && (
                    <div className="absolute top-16 right-4 bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-2 w-56 z-10 border dark:border-gray-700">
                        <button onClick={() => setShowAddMenu('main')} className="flex items-center gap-2 p-2 text-sm font-semibold"><ChevronLeft size={16} /> Wróć</button>
                        <div className="space-y-1 mt-1">
                            {decorationTypes.map(d => <button key={d.id} onClick={() => { addDecoration(d.id); setShowAddMenu(null) }} className="w-full text-left flex items-center gap-2 p-2 hover:bg-cream-100 dark:hover:bg-gray-700 rounded-lg"><d.icon size={16} style={{ color: d.color }} />{d.name}</button>)}
                        </div>
                    </div>
                )}
            </div>
            {(selectedItem || inspectorState.isPinned) && (
                <InspectorWrapper inspectorState={inspectorState} setInspectorState={setInspectorState} setSelectedItem={setSelectedItem}>
                    <InspectorPanel
                        selectedItem={selectedItem}
                        setSelectedItem={setSelectedItem}
                        tables={tables} setTables={setTables}
                        decorations={decorations} setDecorations={setDecorations}
                        guests={guests} setGuests={setGuests}
                        showNotification={showNotification}
                        layout={inspectorState.position === 'bottom' ? 'horizontal' : 'vertical'}
                    />
                </InspectorWrapper>
            )}
            {showSeatPicker && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowSeatPicker(false)}>
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-2xl border border-gray-200 dark:border-gray-700 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
                        <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">Wybierz gościa dla miejsca {selectedSeat.seatNumber}</h3>
                        <div className="max-h-60 overflow-y-auto space-y-2 mb-4 p-1">
                            {guests.filter(g => !g.tableId && g.wedding).length > 0 ? guests.filter(g => !g.tableId).map(guest => (
                                <button key={guest.id} onClick={() => { assignGuestToSeat(guest.id, selectedSeat.tableId, selectedSeat.seatNumber); setShowSeatPicker(false); }}
                                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl transition-all">
                                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: guest.color }}>
                                        {guest.firstName[0]}{guest.lastName[0]}
                                    </div>
                                    <div>
                                        <div className="font-medium text-gray-900 dark:text-gray-100">{guest.firstName} {guest.lastName}</div>
                                    </div>
                                </button>
                            )) : <p className="text-center text-gray-500 py-4">Brak wolnych gości do przypisania.</p>}
                        </div>
                    </div>
                </div>
            )}
            {showResetLayoutConfirm && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/65 backdrop-blur-sm" onClick={() => setShowResetLayoutConfirm(false)}>
                    <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-2xl border border-gray-200 dark:border-gray-700 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Reset układu sali?</h3>
                        <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                            Ta akcja usunie ustawienie stołów i dekoracji oraz wypnie wszystkich gości z miejsc.
                            Lista gości i inne zakładki pozostaną bez zmian.
                        </p>
                        <div className="mt-6 flex items-center justify-end gap-3">
                            <button
                                onClick={() => setShowResetLayoutConfirm(false)}
                                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 font-semibold"
                            >
                                Anuluj
                            </button>
                            <button
                                onClick={handleResetLayout}
                                className="px-4 py-2 rounded-lg bg-gradient-to-r from-red-500 to-rose-600 text-white font-semibold shadow-lg"
                            >
                                Resetuj salę
                            </button>
                        </div>
                    </div>
                </div>
            )}
       </div>
    );
};

const GuestListSidebar = React.memo(({ guests, tables, guestListView, setGuestListView, handleAiSeatAssign, isAiSeatingLoading, selectedItem, hoveredTableId, setHoveredTableId, setSelectedItem, removeGuestFromSeat }) => {
    const handleDragStartGuest = (e, item) => e.dataTransfer.setData('application/json', JSON.stringify({ ...item, type: 'guest' }));
    const guestsToShow = useFilteredGuests(guests, guestListView, "");
    const groupedByTables = useMemo(() => {
        if (guestListView !== 'by-tables') return [];
        const tableMap = tables.reduce((acc, table) => {
            acc[table.id] = { ...table, guests: [] };
            return acc;
        }, {});
        guests.filter(g => g.tableId).forEach(guest => {
            if (tableMap[guest.tableId]) {
                tableMap[guest.tableId].guests.push(guest);
            }
        });
        return Object.values(tableMap).filter(t => t.guests.length > 0 || tables.some(table => table.name === t.name));
    }, [guests, tables, guestListView]);

    return (
        <div className="w-80 h-full bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-2xl overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200/30 dark:border-gray-700/30">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2"><Users size={20} className="text-blue-500" />Goście</h3>
                <div className="flex gap-1 bg-cream-200/60 dark:bg-gray-700/60 rounded-xl p-1">
                    {[{ id: 'unassigned', label: 'Wolni' }, { id: 'all', label: 'Wszyscy' }, { id: 'by-tables', label: 'Stoły' }].map(view => (
                        <button key={view.id} onClick={() => setGuestListView(view.id)} className={`flex-1 px-2 py-1.5 text-xs font-bold rounded-lg transition-all ${guestListView === view.id ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`}>{view.label}</button>
                    ))}
                </div>
                 <button onClick={handleAiSeatAssign} disabled={isAiSeatingLoading || guests.filter(g => !g.tableId && g.wedding).length === 0} className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-lg shadow-lg hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                    {isAiSeatingLoading ? (<><div className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin"></div>Myślę...</>) : (<><Sparkles size={16} />Rozsadź Gości ✨</>)}
                </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-2">
                {guestListView === 'by-tables' ? (groupedByTables.length > 0 ? groupedByTables.map((tableData) => {
                    const isSelected = selectedItem?.type === 'table' && selectedItem?.id === tableData?.id;
                    return (
                        <div key={tableData.name} className={`p-2 rounded-lg transition-colors duration-300 ${isSelected ? 'bg-blue-100 dark:bg-blue-900/40' : (hoveredTableId === tableData?.id ? 'bg-gray-100 dark:bg-gray-700/40' : 'bg-transparent')}`}
                            onMouseEnter={() => setHoveredTableId(tableData?.id)} onMouseLeave={() => setHoveredTableId(null)} >
                            <h4 onClick={() => setSelectedItem({ type: 'table', id: tableData?.id })} className="text-xs font-bold text-gray-500 dark:text-gray-400 px-2 py-1 uppercase tracking-wider cursor-pointer">{tableData.name} ({tableData.guests.length})</h4>
                            <div className="space-y-1 mt-1">
                                {tableData.guests.sort((a,b)=>a.seatNumber-b.seatNumber).map(guest => <GuestCard key={guest.id} guest={guest} onDragStart={handleDragStartGuest} removeGuestFromSeat={removeGuestFromSeat} />)}
                            </div>
                        </div>
                    )
                }) : <p className="text-center text-gray-500 py-4">Brak przypisanych gości.</p>) 
                : (guestsToShow.length > 0 ? guestsToShow.map(guest => <GuestCard key={guest.id} guest={guest} onDragStart={handleDragStartGuest} />) : <p className="text-center text-gray-500 py-4">Brak gości do wyświetlenia</p>)}
            </div>
        </div>
    );
});

const GuestCard = React.memo(({ guest, onDragStart, removeGuestFromSeat }) => (
    <div className="relative flex items-center gap-3 p-2 bg-white/80 dark:bg-gray-700/80 rounded-xl border border-transparent hover:border-blue-400/50 cursor-move hover:shadow-lg transition-all duration-200 hover:scale-[1.02] backdrop-blur-sm group" draggable onDragStart={(e) => onDragStart(e, guest)}>
        <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-bold shadow-lg ring-2 ring-white/30" style={{ backgroundColor: guest.color }}>{guest.firstName[0]}{guest.lastName[0]}</div>
        <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-gray-900 dark:text-gray-100 truncate">{guest.firstName} {guest.lastName}</div>
            {guest.plusOne && (<div className="text-xs text-purple-600 dark:text-purple-400 flex items-center gap-1 mt-0.5"><UserPlus size={10} />{guest.plusOne}</div>)}
        </div>
        {guest.children > 0 && (<div className="flex-shrink-0 flex items-center gap-1 px-2 py-1 bg-orange-100/60 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full"><Baby size={12} /><span className="text-xs font-semibold">{guest.children}</span></div>)}
        {removeGuestFromSeat && (
            <button onClick={() => removeGuestFromSeat(guest.id)} className="absolute top-1 right-1 p-0.5 bg-red-500/80 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600">
                <X size={12} />
            </button>
        )}
    </div>
));

const DecorationItem = React.memo(({ decoration, isSelected, onMouseDown, itemRef, darkMode, decorationTypes, interactionState }) => {
    const { width, height, color, name, type, rotation } = decoration;
    const DecorationType = decorationTypes.find(dt => dt.id === type);
    const IconComponent = DecorationType?.icon || Sparkles;
    
    const handleSize = 8;
    const handles = [
        { x: -width / 2, y: -height / 2, cursor: 'nwse-resize', type: 'resize-tl' }, { x: width / 2, y: -height / 2, cursor: 'nesw-resize', type: 'resize-tr' },
        { x: width / 2, y: height / 2, cursor: 'nwse-resize', type: 'resize-br' }, { x: -width / 2, y: height / 2, cursor: 'nesw-resize', type: 'resize-bl' },
        { x: 0, y: -height / 2, cursor: 'ns-resize', type: 'resize-t' }, { x: width / 2, y: 0, cursor: 'ew-resize', type: 'resize-r' },
        { x: 0, y: height / 2, cursor: 'ns-resize', type: 'resize-b' }, { x: -width / 2, y: 0, cursor: 'ew-resize', type: 'resize-l' },
    ];

    return (
        <g ref={itemRef} transform={`translate(${decoration.x} ${decoration.y}) rotate(${rotation || 0})`} onMouseDown={(e) => onMouseDown(e, decoration, 'decoration')} data-item-id={decoration.id} data-item-type="decoration">
             <rect x={-width / 2} y={-height / 2} width={width} height={height} rx="8" fill={color + '20'} stroke={isSelected ? '#3b82f6' : color} strokeWidth="2" strokeDasharray="5 5" className={decoration.isLocked ? '' : 'cursor-move'} />
             <foreignObject x={-width / 2} y={-height / 2} width={width} height={height} className="pointer-events-none">
                 <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-center">
                     <IconComponent size={Math.min(24, width/4, height/4)} color={color} />
                     <span className={`text-[10px] font-bold select-none ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{name}</span>
                 </div>
             </foreignObject>
             {isSelected && !decoration.isLocked && (
                <>
                    <g onMouseDown={(e) => onMouseDown(e, decoration, 'decoration', 'rotate')} className="cursor-alias">
                        <line x1="0" y1={-height / 2} x2="0" y2={-height / 2 - 20} stroke="#3b82f6" strokeWidth="2" />
                        <circle cx="0" cy={-height / 2 - 20} r="6" fill="#fff" stroke="#3b82f6" strokeWidth="2" />
                    </g>
                    {handles.map(h => (
                        <rect key={h.type} x={h.x - handleSize / 2} y={h.y - handleSize / 2} width={handleSize} height={handleSize} fill="#fff" stroke="#3b82f6" strokeWidth="2" style={{ cursor: h.cursor }} onMouseDown={e => onMouseDown(e, decoration, 'decoration', h.type)} />
                    ))}
                </>
            )}
        </g>
    );
});

const TableItem = React.memo(({ table, guests, isSelected, isHovered, onMouseDown, itemRef, darkMode, interactionState, removeGuestFromSeat, setShowSeatPicker, setSelectedSeat, showNotification, setHoveredTableId, assignGuestToSeat }) => {
    const occupiedSeats = guests.filter(g => g.tableId === table.id).length;
    const handleDropOnSeat = (e, targetTable, seatNumber) => {
        e.preventDefault(); e.stopPropagation();
        try {
            const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
            if (dragData.type === 'guest') {
                const existingGuest = guests.find(g => g.tableId === targetTable.id && g.seatNumber === seatNumber);
                if (existingGuest) { showNotification('To miejsce jest już zajęte!', 'error'); }
                else { assignGuestToSeat(dragData.id, targetTable.id, seatNumber); }
            }
        } catch (error) { console.error('Error handling drop:', error); }
    };
    const handleSeatClick = (e, seatNumber) => {
        e.stopPropagation();
        if (table.isLocked) return;
        const existingGuest = guests.find(g => g.tableId === table.id && g.seatNumber === seatNumber);
        if (!existingGuest) { setSelectedSeat({ tableId: table.id, seatNumber }); setShowSeatPicker(true); }
    };
    
    const handleRemoveGuestClick = (e, guestId) => {
        e.stopPropagation();
        removeGuestFromSeat(guestId);
    };

    const wrapText = (text, maxWidth) => {
        if (!text) return [];
        const words = text.split(' ');
        let lines = [];
        let currentLine = words[0] || '';

        for (let i = 1; i < words.length; i++) {
             if ((currentLine.length + words[i].length) < (maxWidth / 7)) { // Heuristic for average char width
                currentLine += ` ${words[i]}`;
            } else {
                lines.push(currentLine);
                currentLine = words[i];
            }
        }
        lines.push(currentLine);
        return lines;
    }

    const nameLines = wrapText(table.name, table.shape === 'circle' ? table.radius * 1.5 : table.width * 0.9);
    const lineHeight = 16;
    const totalNameHeight = nameLines.length * lineHeight;
    const startY = - (totalNameHeight / 2) + (lineHeight / 2);

    return (
        <g ref={itemRef} transform={`translate(${table.x} ${table.y}) rotate(${table.rotation || 0})`} data-item-id={table.id}>
            {table.shape === 'circle' ? (
                <circle cx="0" cy="0" r={table.radius} onMouseDown={(e) => onMouseDown(e, table, 'table')} fill={darkMode ? "#1e293b" : "#fff"} stroke={isSelected ? '#3b82f6' : (darkMode ? table.color : '#cbd5e1')} strokeWidth={isSelected ? 4 : 2} className={`${table.isLocked ? '' : 'cursor-move'}`} />
            ) : (
                <rect x={-table.width / 2} y={-table.height / 2} width={table.width} height={table.height} rx="8" onMouseDown={(e) => onMouseDown(e, table, 'table')} fill={darkMode ? "#1e293b" : "#fff"} stroke={isSelected ? '#3b82f6' : (darkMode ? table.color : '#cbd5e1')} strokeWidth={isSelected ? 4 : 2} className={`${table.isLocked ? '' : 'cursor-move'}`} />
            )}
             <text textAnchor="middle" className={`text-sm font-bold select-none pointer-events-none ${darkMode ? 'fill-gray-200' : 'fill-gray-800'}`}>
                {nameLines.map((line, i) => (
                    <tspan key={i} x="0" y={startY + (i * lineHeight) - 8}>{line}</tspan>
                ))}
            </text>
            <text x="0" y={startY + totalNameHeight - 8} textAnchor="middle" className={`text-xs select-none pointer-events-none ${darkMode ? 'fill-gray-400' : 'fill-gray-500'}`}>{occupiedSeats} / {table.seats} miejsc</text>

            {Array.from({ length: table.seats }).map((_, index) => {
                const seatNumber = index + 1;
                const guest = guests.find(g => g.tableId === table.id && g.seatNumber === seatNumber);
                let seatX, seatY;
                if (table.shape === 'circle') {
                    const angle = (index * 360 / table.seats) * Math.PI / 180;
                    seatX = Math.cos(angle) * (table.radius + 30);
                    seatY = Math.sin(angle) * (table.radius + 30);
                } else {
                    const seatsTop = Math.ceil(table.seats / 2);
                    const seatsBottom = table.seats - seatsTop;
                    const spacingTop = table.width / (seatsTop + 1);
                    const spacingBottom = table.width / (seatsBottom + 1);
                    if (index < seatsTop) {
                        seatX = -table.width / 2 + spacingTop * (index + 1);
                        seatY = -table.height / 2 - 30;
                    } else {
                        seatX = -table.width / 2 + spacingBottom * (index - seatsTop + 1);
                        seatY = table.height / 2 + 30;
                    }
                }
                return (
                    <g key={index} transform={`translate(${seatX}, ${seatY})`} className="seat-group group" onDrop={e => !table.isLocked && handleDropOnSeat(e, table, seatNumber)} onDragOver={(e)=>e.preventDefault()} onClick={(e) => handleSeatClick(e, seatNumber)}>
                        <circle r="20" fill={guest ? guest.color : (darkMode ? "#334155" : "#e2e8f0")} stroke={darkMode ? "#475569" : "#cbd5e1"} strokeWidth="2" className="transition-all hover:scale-110 cursor-pointer" />
                        <text x="0" y="5" textAnchor="middle" className="text-xs font-bold pointer-events-none" fill={guest ? "#fff" : (darkMode ? "#94a3b8" : "#64748b")}>
                            {guest ? `${guest.firstName[0]}${guest.lastName[0]}` : seatNumber}
                        </text>
                         {guest && !table.isLocked && (
                            <g transform="translate(15, -15)" className="cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity" style={{ pointerEvents: 'auto' }} onClick={e => handleRemoveGuestClick(e, guest.id)}>
                                <circle r="8" className="fill-red-500" />
                                <X x="-4" y="-4" size={8} className="text-white pointer-events-none" />
                            </g>
                        )}
                    </g>
                );
            })}
        </g>
    );
});

const InspectorWrapper = ({ children, inspectorState, setInspectorState, setSelectedItem }) => {
    const { position, isMinimized, isPinned } = inspectorState;
    const wrapperClasses = position === 'right' ? 'absolute top-0 right-0 h-full p-6 pointer-events-none' : 'absolute bottom-0 left-0 w-full p-4 pointer-events-none';
    const panelClasses = position === 'right' ? `w-80 h-full flex flex-col ${isMinimized ? 'h-12' : ''}` : `w-full max-w-4xl mx-auto flex flex-col ${isMinimized ? 'h-12' : ''}`;
    
    const handleClose = () => {
        setInspectorState(prev => ({ ...prev, isPinned: false }));
        setSelectedItem(null);
    }

    return (
        <div className={wrapperClasses}>
            <div className={`pointer-events-auto bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl border border-cream-200/50 dark:border-gray-700/50 shadow-2xl transition-all duration-300 overflow-hidden ${panelClasses}`}>
                <div className="p-3 border-b border-cream-200/50 dark:border-gray-700/50 flex items-center justify-between h-12 flex-shrink-0">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Inspektor</h3>
                    <div className="flex items-center gap-2">
                        <button onClick={() => setInspectorState(prev => ({ ...prev, position: prev.position === 'right' ? 'bottom' : 'right' }))} className="p-1 rounded-full hover:bg-cream-100 dark:hover:bg-gray-700" title={position === 'right' ? "Przesuń na dół" : "Przesuń w prawo"}>{position === 'right' ? <ArrowDownToLine size={16} /> : <ArrowRightToLine size={16} />}</button>
                        <button onClick={() => setInspectorState(prev => ({ ...prev, isPinned: !prev.isPinned }))} className="p-1 rounded-full hover:bg-cream-100 dark:hover:bg-gray-700" title={isPinned ? "Odepnij" : "Przypnij"}>{isPinned ? <PinOff size={16} className="text-rose-500" /> : <Pin size={16} />}</button>
                        <button onClick={() => setInspectorState(prev => ({ ...prev, isMinimized: !prev.isMinimized }))} className="p-1 rounded-full hover:bg-cream-100 dark:hover:bg-gray-700" title={isMinimized ? "Pokaż" : "Ukryj"}>{isMinimized ? <Maximize2 size={16} /> : <MinusSquare size={16} />}</button>
                        <button onClick={handleClose} className="p-1 rounded-full hover:bg-cream-100 dark:hover:bg-gray-700"><X size={16} /></button>
                    </div>
                </div>
                {!isMinimized && children}
            </div>
        </div>
    )
}

const InspectorPanel = ({ selectedItem, setSelectedItem, tables, setTables, decorations, setDecorations, guests, setGuests, showNotification, layout }) => {
    const item = useMemo(() => {
        if (!selectedItem) return null;
        return selectedItem.type === 'table' ? tables.find(t => String(t.id) === String(selectedItem.id)) : decorations.find(d => String(d.id) === String(selectedItem.id));
    }, [selectedItem, tables, decorations]);

    const [name, setName] = useState(item?.name || '');
    const debouncedName = useDebounce(name, 300);

    useEffect(() => { if (item) { setName(item.name); } }, [item]);

    const handleUpdate = useCallback((updateData) => {
        const stateUpdater = selectedItem.type === 'table' ? setTables : setDecorations;
        stateUpdater(prevItems => prevItems.map(i => i.id === selectedItem.id ? { ...i, ...updateData } : i));
    }, [selectedItem, setTables, setDecorations]);

    const removeGuestFromSeat = useCallback((guestId) => {
        setGuests(prevGuests => prevGuests.map(guest => 
            guest.id === guestId ? { ...guest, tableId: null, seatNumber: null, tableAssigned: false } : guest
        ));
    }, [setGuests]);

    useEffect(() => {
        if (item && debouncedName !== item.name && debouncedName.trim() !== '') {
            handleUpdate({ name: debouncedName });
        }
    }, [debouncedName, item, handleUpdate]);


    if (!item) return null;

    const deleteItem = () => {
        if (selectedItem.type === 'table') {
            setTables(prev => prev.filter(t => t.id !== selectedItem.id));
             setGuests(prevGuests => prevGuests.map(g => g.tableId === selectedItem.id ? { ...g, tableId: null, seatNumber: null, tableAssigned: false } : g));
        } else {
            setDecorations(prev => prev.filter(d => d.id !== selectedItem.id));
        }
        showNotification(`${item.name} usunięto.`, 'info');
        setSelectedItem(null);
    };

    const isTable = selectedItem.type === 'table';
    const isDecoration = selectedItem.type === 'decoration';
    const assignedGuests = isTable ? guests.filter(g => g.tableId === item.id).sort((a,b) => a.seatNumber - b.seatNumber) : [];
    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

    const contentClasses = layout === 'horizontal' ? 'flex-row p-2 gap-4' : 'flex-col p-4 space-y-4';

    return (
        <div className={`flex-1 overflow-auto flex ${contentClasses}`}>
            <div className="flex-grow space-y-4">
                <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400">NAZWA</label>
                    <input type="text" value={name} onChange={e => setName(e.target.value)} className="mt-1 w-full p-2 bg-cream-100 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600" />
                </div>
                {isTable && (
                    <>
                        <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">MIEJSCA</label>
                            <div className="flex items-center gap-2 mt-1">
                                <button onClick={() => handleUpdate({ seats: Math.max(1, item.seats - 1) })} className="p-2 bg-cream-200 dark:bg-gray-600 rounded-full"><Minus size={12} /></button>
                                <input type="number" value={item.seats} onChange={e => handleUpdate({ seats: parseInt(e.target.value) || 0 })} className="w-full p-2 text-center bg-cream-100 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600" />
                                <button onClick={() => handleUpdate({ seats: item.seats + 1 })} className="p-2 bg-cream-200 dark:bg-gray-600 rounded-full"><Plus size={12} /></button>
                            </div>
                        </div>
                        <div>
                             <label className="text-xs font-medium text-gray-500 dark:text-gray-400">KSZTAŁT</label>
                             <div className="grid grid-cols-2 gap-2 mt-1">
                                 <button onClick={() => handleUpdate({ shape: 'circle', radius: item.radius || 80, width: null, height: null })} className={`p-2 rounded-lg border-2 ${item.shape === 'circle' ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/50' : 'border-gray-200 dark:border-gray-600'}`}><Circle className="mx-auto" /></button>
                                 <button onClick={() => handleUpdate({ shape: 'rectangle', width: item.width || 160, height: item.height || 80, radius: null })} className={`p-2 rounded-lg border-2 ${item.shape === 'rectangle' ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/50' : 'border-gray-200 dark:border-gray-600'}`}><TableProperties className="mx-auto" /></button>
                             </div>
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">ROZMIAR STOŁU</label>
                            {item.shape === 'circle' ? (
                                <div className="space-y-2 mt-1">
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleUpdate({ radius: clamp((item.radius || 80) - 10, 40, 260) })}
                                            className="p-2 bg-cream-200 dark:bg-gray-600 rounded-full"
                                        >
                                            <Minus size={12} />
                                        </button>
                                        <input
                                            type="number"
                                            min="40"
                                            max="260"
                                            value={Math.round(item.radius || 80)}
                                            onChange={e => handleUpdate({ radius: clamp(parseInt(e.target.value) || 80, 40, 260) })}
                                            className="w-full p-2 text-center bg-cream-100 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
                                        />
                                        <button
                                            onClick={() => handleUpdate({ radius: clamp((item.radius || 80) + 10, 40, 260) })}
                                            className="p-2 bg-cream-200 dark:bg-gray-600 rounded-full"
                                        >
                                            <Plus size={12} />
                                        </button>
                                    </div>
                                    <input
                                        type="range"
                                        min="40"
                                        max="260"
                                        value={item.radius || 80}
                                        onChange={e => handleUpdate({ radius: clamp(parseInt(e.target.value), 40, 260) })}
                                        className="w-full"
                                    />
                                </div>
                            ) : (
                                <div className="space-y-3 mt-1">
                                    <div>
                                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                                            <span>Szerokość</span>
                                            <span>{Math.round(item.width || 160)} px</span>
                                        </div>
                                        <input
                                            type="range"
                                            min="80"
                                            max="900"
                                            value={item.width || 160}
                                            onChange={e => handleUpdate({ width: clamp(parseInt(e.target.value), 80, 900) })}
                                            className="w-full"
                                        />
                                    </div>
                                    <div>
                                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                                            <span>Wysokość</span>
                                            <span>{Math.round(item.height || 80)} px</span>
                                        </div>
                                        <input
                                            type="range"
                                            min="50"
                                            max="280"
                                            value={item.height || 80}
                                            onChange={e => handleUpdate({ height: clamp(parseInt(e.target.value), 50, 280) })}
                                            className="w-full"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">OBRÓT</label>
                            <div className="flex items-center gap-2 mt-1">
                                <input
                                    type="range"
                                    min="0"
                                    max="360"
                                    value={item.rotation || 0}
                                    onChange={e => handleUpdate({ rotation: parseFloat(e.target.value) })}
                                    className="w-full"
                                />
                                <span className="text-sm font-semibold w-12 text-center">{Math.round(item.rotation || 0)}°</span>
                            </div>
                            <div className="grid grid-cols-3 gap-2 mt-2">
                                <button
                                    onClick={() => handleUpdate({ rotation: ((item.rotation || 0) - 15 + 360) % 360 })}
                                    className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-semibold"
                                >
                                    -15°
                                </button>
                                <button
                                    onClick={() => handleUpdate({ rotation: ((item.rotation || 0) + 15) % 360 })}
                                    className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-semibold"
                                >
                                    +15°
                                </button>
                                <button
                                    onClick={() => handleUpdate({ rotation: ((item.rotation || 0) + 180) % 360 })}
                                    className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-semibold"
                                >
                                    Odwróć
                                </button>
                            </div>
                        </div>
                    </>
                )}
                 {isDecoration && (
                    <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400">OBRÓT</label>
                        <div className="flex items-center gap-2 mt-1">
                            <input type="range" min="0" max="360" value={item.rotation || 0} onChange={e => handleUpdate({ rotation: parseFloat(e.target.value) })} className="w-full" />
                            <span className="text-sm font-semibold w-12 text-center">{Math.round(item.rotation || 0)}°</span>
                        </div>
                    </div>
                )}
                 {isTable && (
                    <div className="pt-2">
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400">GOŚCIE PRZY STOLE ({assignedGuests.length}/{item.seats})</label>
                        <div className="mt-2 space-y-2 max-h-48 overflow-y-auto pr-2">
                            {assignedGuests.map(guest => (
                                <div key={guest.id} className="flex items-center justify-between p-2 bg-cream-100 dark:bg-gray-700/50 rounded-lg">
                                    <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: guest.color }}>{guest.firstName[0]}{guest.lastName[0]}</div>
                                        <span className="text-sm font-medium">{guest.firstName} {guest.lastName}</span>
                                    </div>
                                    <button onClick={() => removeGuestFromSeat(guest.id)} className="p-1 rounded-full hover:bg-red-200/50 dark:hover:bg-red-800/50 text-red-500">
                                        <X size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <div className="p-4 border-t border-cream-200/50 dark:border-gray-700/50 mt-auto flex gap-2 flex-shrink-0">
                <button onClick={() => handleUpdate({ isLocked: !item.isLocked })} className="p-2 bg-cream-200 dark:bg-gray-700 rounded-lg" title={item.isLocked ? "Odblokuj" : "Zablokuj"}>
                    {item.isLocked ? <Lock size={16} /> : <Unlock size={16} />}
                </button>
                <button onClick={deleteItem} className="flex-1 bg-red-500 text-white p-2 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-red-600 transition-colors">
                    <Trash2 size={16} /> Usuń
                </button>
            </div>
        </div>
    );
};
// #endregion

// #region --- Auth & Onboarding ---
const AuthLanding = ({ onLogin, onRegister, isSubmitting }) => {
    const [mode, setMode] = useState('login');
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [localError, setLocalError] = useState('');

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLocalError('');

        const normalizedEmail = email.trim().toLowerCase();
        if (!normalizedEmail || !password) {
            setLocalError('Uzupełnij email i hasło.');
            return;
        }
        if (mode === 'register' && !fullName.trim()) {
            setLocalError('Podaj swoje imię.');
            return;
        }
        if (password.length < 6) {
            setLocalError('Hasło musi mieć co najmniej 6 znaków.');
            return;
        }

        try {
            if (mode === 'login') {
                await onLogin({ email: normalizedEmail, password });
            } else {
                await onRegister({ fullName: fullName.trim(), email: normalizedEmail, password });
            }
        } catch (error) {
            setLocalError(error.message || 'Wystąpił błąd logowania.');
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-rose-100 via-orange-50 to-indigo-100 text-gray-900">
            <div className="max-w-6xl mx-auto min-h-screen grid lg:grid-cols-2 gap-6 p-6 lg:p-10">
                <div className="relative overflow-hidden rounded-3xl p-8 lg:p-12 bg-gradient-to-br from-rose-600 via-orange-500 to-amber-500 text-white shadow-2xl">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.25),transparent_40%)]"></div>
                    <div className="relative space-y-8">
                        <div className="flex items-center gap-3">
                            <Heart className="w-9 h-9" />
                            <h1 className="text-4xl font-black tracking-tight">Weddinger</h1>
                        </div>
                        <div>
                            <p className="text-sm uppercase tracking-[0.3em] text-white/80">Planer ślubny</p>
                            <h2 className="text-4xl lg:text-5xl font-black leading-tight mt-2">
                                Ślub pod kontrolą.
                                <br />
                                Bez chaosu.
                            </h2>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div className="bg-white/20 rounded-2xl p-4 backdrop-blur-sm border border-white/25">
                                <Sparkles className="mb-2" />
                                <p className="font-bold">Plan stołów i gości</p>
                                <p className="text-sm text-white/90">Drag&drop, podział na stoliki, szybka edycja.</p>
                            </div>
                            <div className="bg-white/20 rounded-2xl p-4 backdrop-blur-sm border border-white/25">
                                <Target className="mb-2" />
                                <p className="font-bold">Zadania i budżet</p>
                                <p className="text-sm text-white/90">Widok postępu i wydatków w jednym miejscu.</p>
                            </div>
                        </div>
                        <p className="text-white/90">
                            Wszystko w jednym miejscu, prosto i spokojnie. Zaplanujcie swój dzień krok po kroku, bez stresu.
                        </p>
                    </div>
                </div>

                <div className="bg-white/85 backdrop-blur-xl rounded-3xl border border-white/60 shadow-2xl p-6 lg:p-8 self-center">
                    <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-xl mb-6">
                        <button
                            onClick={() => setMode('register')}
                            className={`flex-1 px-3 py-2 rounded-lg font-semibold transition-all ${mode === 'register' ? 'bg-white shadow text-rose-600' : 'text-gray-500'}`}
                        >
                            Rejestracja
                        </button>
                        <button
                            onClick={() => setMode('login')}
                            className={`flex-1 px-3 py-2 rounded-lg font-semibold transition-all ${mode === 'login' ? 'bg-white shadow text-rose-600' : 'text-gray-500'}`}
                        >
                            Logowanie
                        </button>
                    </div>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {mode === 'register' && (
                            <div>
                                <label className="text-sm font-medium text-gray-600">Jak masz na imię?</label>
                                <div className="mt-1 flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2 bg-white">
                                    <User size={16} className="text-gray-400" />
                                    <input
                                        type="text"
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                        placeholder="Np. Kasia"
                                        className="w-full bg-transparent focus:outline-none"
                                    />
                                </div>
                            </div>
                        )}
                        <div>
                            <label className="text-sm font-medium text-gray-600">Email</label>
                            <div className="mt-1 flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2 bg-white">
                                <Mail size={16} className="text-gray-400" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="ty@email.com"
                                    className="w-full bg-transparent focus:outline-none"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Hasło</label>
                            <div className="mt-1 flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2 bg-white">
                                <Lock size={16} className="text-gray-400" />
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Minimum 6 znaków"
                                    className="w-full bg-transparent focus:outline-none"
                                />
                            </div>
                        </div>
                        {localError && (
                            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-3">
                                {localError}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full py-3 rounded-xl text-white font-bold bg-gradient-to-r from-rose-500 to-orange-500 shadow-lg disabled:opacity-60"
                        >
                            {isSubmitting ? 'Przetwarzanie...' : mode === 'register' ? 'Stwórz konto i zacznij' : 'Wejdź do planera'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

const OnboardingWizard = ({ defaultName, onComplete, isSubmitting, canImportLegacy, onImportLegacy, isImportingLegacy }) => {
    const [step, setStep] = useState(0);
    const [error, setError] = useState('');
    const [form, setForm] = useState({
        yourName: defaultName || '',
        yourRole: 'couple',
        brideName: '',
        groomName: '',
        weddingDate: '',
        totalBudget: '',
        expectedGuests: '',
        weddingStyle: '',
        weddingLocation: '',
    });

    const steps = ['Ty', 'Para', 'Data i budżet', 'Klimat'];

    const validateCurrentStep = () => {
        if (step === 0 && !form.yourName.trim()) return 'Podaj swoje imię.';
        if (step === 1 && (!form.brideName.trim() || !form.groomName.trim())) return 'Podaj imię Panny i Pana Młodego.';
        if (step === 2 && !form.weddingDate) return 'Wybierz datę ślubu.';
        return '';
    };

    const handleNext = () => {
        const validationError = validateCurrentStep();
        if (validationError) {
            setError(validationError);
            return;
        }
        setError('');
        setStep((prev) => Math.min(prev + 1, steps.length - 1));
    };

    const handleBack = () => {
        setError('');
        setStep((prev) => Math.max(prev - 1, 0));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const validationError = validateCurrentStep();
        if (validationError) {
            setError(validationError);
            return;
        }
        setError('');
        await onComplete(form);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-rose-50 to-amber-100 p-4 md:p-8">
            <div className="max-w-4xl mx-auto bg-white/80 backdrop-blur-2xl rounded-3xl border border-white/70 shadow-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-rose-500 text-white p-6 md:p-8">
                    <p className="uppercase tracking-[0.25em] text-xs text-white/80">Pierwsze uruchomienie</p>
                    <h2 className="text-3xl md:text-4xl font-black mt-2">Skonfiguruj swój planer ślubny</h2>
                    <p className="mt-2 text-white/90">Po tym kroku dostaniesz gotową aplikację pod Wasz ślub.</p>
                    <div className="mt-5 grid grid-cols-4 gap-2">
                        {steps.map((label, index) => (
                            <div key={label} className={`rounded-lg px-3 py-2 text-sm font-semibold text-center ${index <= step ? 'bg-white text-indigo-700' : 'bg-white/20 text-white'}`}>
                                {label}
                            </div>
                        ))}
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="p-6 md:p-8">
                    {step === 0 && (
                        <div className="space-y-5">
                            <div>
                                <label className="text-sm font-medium text-gray-600">Jak masz na imię?</label>
                                <input
                                    type="text"
                                    value={form.yourName}
                                    onChange={(e) => setForm((prev) => ({ ...prev, yourName: e.target.value }))}
                                    placeholder="Np. Karolina"
                                    className="mt-1 w-full p-3 rounded-xl border border-gray-200 bg-white"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">Twoja rola</label>
                                <div className="mt-2 grid sm:grid-cols-2 gap-2">
                                    {[
                                        { id: 'bride', label: 'Panna Młoda' },
                                        { id: 'groom', label: 'Pan Młody' },
                                        { id: 'couple', label: 'Para' },
                                        { id: 'planner', label: 'Wedding planner' },
                                    ].map((role) => (
                                        <button
                                            type="button"
                                            key={role.id}
                                            onClick={() => setForm((prev) => ({ ...prev, yourRole: role.id }))}
                                            className={`px-3 py-2 rounded-xl border-2 font-semibold ${form.yourRole === role.id ? 'border-rose-500 bg-rose-50 text-rose-600' : 'border-gray-200 text-gray-600'}`}
                                        >
                                            {role.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 1 && (
                        <div className="grid md:grid-cols-2 gap-5">
                            <div>
                                <label className="text-sm font-medium text-gray-600">Imię Panny Młodej</label>
                                <input
                                    type="text"
                                    value={form.brideName}
                                    onChange={(e) => setForm((prev) => ({ ...prev, brideName: e.target.value }))}
                                    className="mt-1 w-full p-3 rounded-xl border border-gray-200 bg-white"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">Imię Pana Młodego</label>
                                <input
                                    type="text"
                                    value={form.groomName}
                                    onChange={(e) => setForm((prev) => ({ ...prev, groomName: e.target.value }))}
                                    className="mt-1 w-full p-3 rounded-xl border border-gray-200 bg-white"
                                />
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="grid md:grid-cols-3 gap-5">
                            <div>
                                <label className="text-sm font-medium text-gray-600">Data ślubu</label>
                                <input
                                    type="date"
                                    value={form.weddingDate}
                                    onChange={(e) => setForm((prev) => ({ ...prev, weddingDate: e.target.value }))}
                                    className="mt-1 w-full p-3 rounded-xl border border-gray-200 bg-white"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">Budżet całkowity (PLN)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={form.totalBudget}
                                    onChange={(e) => setForm((prev) => ({ ...prev, totalBudget: e.target.value }))}
                                    className="mt-1 w-full p-3 rounded-xl border border-gray-200 bg-white"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">Planowana liczba gości</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={form.expectedGuests}
                                    onChange={(e) => setForm((prev) => ({ ...prev, expectedGuests: e.target.value }))}
                                    className="mt-1 w-full p-3 rounded-xl border border-gray-200 bg-white"
                                />
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="grid md:grid-cols-2 gap-5">
                            <div>
                                <label className="text-sm font-medium text-gray-600">Styl wesela</label>
                                <input
                                    type="text"
                                    value={form.weddingStyle}
                                    onChange={(e) => setForm((prev) => ({ ...prev, weddingStyle: e.target.value }))}
                                    placeholder="Np. glamour, boho, klasyczny"
                                    className="mt-1 w-full p-3 rounded-xl border border-gray-200 bg-white"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">Miejsce / miasto</label>
                                <input
                                    type="text"
                                    value={form.weddingLocation}
                                    onChange={(e) => setForm((prev) => ({ ...prev, weddingLocation: e.target.value }))}
                                    placeholder="Np. Kraków"
                                    className="mt-1 w-full p-3 rounded-xl border border-gray-200 bg-white"
                                />
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="mt-5 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-3">
                            {error}
                        </div>
                    )}

                    {canImportLegacy && (
                        <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
                            <p className="text-sm text-amber-900">
                                Masz włączony tryb bezpiecznej migracji legacy. Możesz przenieść swoje stare dane jednym kliknięciem.
                            </p>
                            <button
                                type="button"
                                onClick={onImportLegacy}
                                disabled={isImportingLegacy || isSubmitting}
                                className="mt-3 px-4 py-2 rounded-lg text-sm font-bold bg-amber-500 text-white disabled:opacity-50"
                            >
                                {isImportingLegacy ? 'Przenoszę dane...' : 'Importuj moje dane legacy'}
                            </button>
                        </div>
                    )}

                    <div className="mt-7 flex items-center justify-between">
                        <button
                            type="button"
                            onClick={handleBack}
                            disabled={step === 0}
                            className="px-4 py-2 rounded-lg font-semibold text-gray-600 disabled:opacity-40"
                        >
                            Wstecz
                        </button>
                        {step < steps.length - 1 ? (
                            <button
                                type="button"
                                onClick={handleNext}
                                className="px-5 py-2 rounded-lg font-bold text-white bg-gradient-to-r from-indigo-500 to-rose-500"
                            >
                                Dalej
                            </button>
                        ) : (
                            <button
                                type="submit"
                                disabled={isSubmitting || isImportingLegacy}
                                className="px-5 py-2 rounded-lg font-bold text-white bg-gradient-to-r from-rose-500 to-orange-500 disabled:opacity-60"
                            >
                                {isSubmitting ? 'Tworzę planer...' : 'Uruchom mój planer'}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

const FeedbackWidget = ({ onSubmit, isSubmitting, currentUserEmail }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [category, setCategory] = useState('bug');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [contact, setContact] = useState(currentUserEmail || '');
    const [localError, setLocalError] = useState('');
    const categoryOptions = [
        { id: 'bug', label: 'Błąd' },
        { id: 'idea', label: 'Pomysł' },
        { id: 'improvement', label: 'Usprawnienie' },
    ];

    useEffect(() => {
        if (!isOpen) {
            setLocalError('');
            setContact(currentUserEmail || '');
        }
    }, [currentUserEmail, isOpen]);

    const handleSend = async (event) => {
        event.preventDefault();
        setLocalError('');
        if (!message.trim() || message.trim().length < 10) {
            setLocalError('Napisz proszę trochę więcej szczegółów (minimum 10 znaków).');
            return;
        }

        const payload = {
            category,
            subject: subject.trim(),
            message: message.trim(),
            contact: contact.trim(),
            pageUrl: typeof window !== 'undefined' ? window.location.href : '',
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
            createdAt: new Date().toISOString(),
        };

        const success = await onSubmit(payload);
        if (success) {
            setIsOpen(false);
            setCategory('bug');
            setSubject('');
            setMessage('');
            setLocalError('');
            setContact(currentUserEmail || '');
        }
    };

    return (
        <>
            <button
                onClick={() => setIsOpen(true)}
                className="fixed bottom-6 left-6 z-[250] px-4 py-2 rounded-full bg-gradient-to-r from-rose-500 to-orange-500 text-white font-bold shadow-xl hover:scale-105 transition-transform"
                title="Wyślij opinię"
            >
                FEEDBACK
            </button>
            {isOpen && (
                <div className="fixed inset-0 z-[260] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setIsOpen(false)}>
                    <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-2xl border border-gray-200 dark:border-gray-700 max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Twoja opinia</h3>
                            <button onClick={() => setIsOpen(false)} className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"><X size={18} /></button>
                        </div>
                        <form onSubmit={handleSend} className="space-y-4">
                            <div>
                                <label className="text-sm font-semibold text-gray-800 dark:text-gray-100">Typ wiadomości</label>
                                <div className="mt-2 grid grid-cols-3 gap-2">
                                    {categoryOptions.map((option) => (
                                        <button
                                            key={option.id}
                                            type="button"
                                            onClick={() => setCategory(option.id)}
                                            className={`px-3 py-2 rounded-lg border text-sm font-semibold transition-colors ${
                                                category === option.id
                                                    ? 'bg-rose-500 text-white border-rose-500'
                                                    : 'bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                                            }`}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-semibold text-gray-800 dark:text-gray-100">Temat (opcjonalnie)</label>
                                <input
                                    type="text"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    placeholder="Np. Problem z zapisem listy gości"
                                    className="mt-1 w-full p-2.5 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg border border-gray-300 dark:border-gray-600 placeholder:text-gray-400"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-semibold text-gray-800 dark:text-gray-100">Co się dzieje / co dodać?</label>
                                <textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Opisz krótko problem albo pomysł..."
                                    rows={5}
                                    className="mt-1 w-full p-2.5 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg border border-gray-300 dark:border-gray-600 placeholder:text-gray-400 resize-y"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-semibold text-gray-800 dark:text-gray-100">Twój email (opcjonalnie)</label>
                                <input
                                    type="email"
                                    value={contact}
                                    onChange={(e) => setContact(e.target.value)}
                                    placeholder="żeby można było odpisać"
                                    className="mt-1 w-full p-2.5 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg border border-gray-300 dark:border-gray-600 placeholder:text-gray-400"
                                />
                            </div>
                            {localError && (
                                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-3">
                                    {localError}
                                </div>
                            )}
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-rose-500 to-orange-500 text-white font-semibold rounded-lg shadow-lg disabled:opacity-60"
                            >
                                <Send size={16} />
                                {isSubmitting ? 'Wysyłanie...' : 'Wyślij feedback'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};
// #endregion

// #region --- Main App Component ---
const WeddingerApp = ({ showNotification, appState, setAppState, darkMode, setDarkMode, onShowConfetti, currentUserName, currentUserId, onSignOut, onExportData, onImportData, isImportingData }) => {
    const { guests, tables, decorations, tasks, vendors, expenses, notes, activeSection, weddingSettings } = appState;
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
    
    // Local UI State for SeatingPlan and GuestList
    const [sortField, setSortField] = useState('firstName');
    const [sortDirection, setSortDirection] = useState('asc');
    const [guestListView, setGuestListView] = useState('unassigned');
    const [selectedItem, setSelectedItem] = useState(null);
    const [showGrid, setShowGrid] = useState(true);
    const [showSeatPicker, setShowSeatPicker] = useState(false);
    const [selectedSeat, setSelectedSeat] = useState(null);
    const [showAddMenu, setShowAddMenu] = useState(null);
    const [isAiSeatingLoading, setIsAiSeatingLoading] = useState(false);
    const [inspectorState, setInspectorState] = useState({ position: 'right', isMinimized: false, isPinned: false });

    const helpStorageKey = useMemo(() => currentUserId ? `weddinger-help-v1-${currentUserId}` : null, [currentUserId]);

    useEffect(() => {
        if (!helpStorageKey) return;
        try {
            const disabledAutoOpen = localStorage.getItem(helpStorageKey) === 'disabled';
            if (!disabledAutoOpen) {
                setIsHelpModalOpen(true);
            }
        } catch (error) {
            console.warn('Cannot read help preference.', error);
        }
    }, [helpStorageKey]);

    const disableHelpAutoOpen = useCallback(() => {
        if (helpStorageKey) {
            try {
                localStorage.setItem(helpStorageKey, 'disabled');
            } catch (error) {
                console.warn('Cannot write help preference.', error);
            }
        }
        setIsHelpModalOpen(false);
        showNotification('OK, tutorial nie będzie już wyskakiwał automatycznie.', 'info');
    }, [helpStorageKey, showNotification]);
    
    const toggleTask = useCallback((taskId) => {
        const task = tasks.find(t => t.id === taskId);
        if (task && !task.completed) {
            onShowConfetti();
            showNotification('Świetna robota! Zadanie ukończone!', 'success');
        }
        setAppState.setTasks(prev => prev.map(t => t.id === taskId ? { ...t, completed: !t.completed, completedAt: !t.completed ? new Date().toISOString() : null } : t));
    }, [setAppState, onShowConfetti, tasks, showNotification]);
    
    const updateTask = useCallback((taskId, updatedData) => {
        setAppState.setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...updatedData } : t));
    }, [setAppState]);

    const deleteTask = useCallback((taskId) => {
        setAppState.setTasks(prev => prev.filter(t => t.id !== taskId));
        showNotification('Zadanie usunięte', 'info');
    }, [setAppState, showNotification]);

    const dashboardStats = useMemo(() => {
        const totalGuests = guests.filter(g => g.wedding).length;
        const confirmedGuests = guests.filter(g => g.confirmed === true && g.wedding).length;
        const totalSeats = tables.reduce((sum, t) => sum + t.seats, 0);
        const occupiedSeats = guests.filter(g => g.tableId).length;
        const totalBudget = weddingSettings.totalBudget || 0;
        const spentBudget = expenses.reduce((sum, ex) => sum + ex.cost, 0);

        let daysLeft = '...';
        if (weddingSettings.weddingDate) {
            const diff = new Date(weddingSettings.weddingDate) - new Date();
            daysLeft = Math.ceil(diff / (1000 * 60 * 60 * 24));
            if(daysLeft < 0) daysLeft = "Już po!";
            else if (daysLeft === 0) daysLeft = "Dziś!";
        }

        return {
            guests: { total: totalGuests, confirmed: confirmedGuests },
            seating: { totalSeats, occupiedSeats, freeSeats: totalSeats - occupiedSeats },
            tasks: { 
                total: tasks.length, 
                completed: tasks.filter(t => t.completed).length,
                completion: tasks.length > 0 ? Math.round((tasks.filter(t => t.completed).length / tasks.length) * 100) : 0 
            },
            budget: {
                total: totalBudget,
                spent: spentBudget,
                progress: totalBudget > 0 ? Math.round((spentBudget / totalBudget) * 100) : 0
            },
            daysLeft,
        };
    }, [guests, tables, tasks, expenses, weddingSettings]);

     const navigationItems = useMemo(() => [
        { id: 'dashboard', name: 'Dashboard', icon: BarChart3, hint: 'Podgląd całości: postęp, goście, dni do ślubu i budżet.' },
        { id: 'seating', name: 'Plan Stołów', icon: Table, hint: 'Układ sali: dodawanie, przesuwanie, obracanie i reset stołów.' },
        { id: 'guests', name: 'Lista Gości', icon: Users, hint: 'Zarządzanie gośćmi, statusami RSVP oraz łączenie w pary.' },
        { id: 'tasks', name: 'Zadania', icon: Target, hint: 'Checklista przygotowań: co jest zrobione, co jeszcze przed Wami.' },
        { id: 'vendors', name: 'Dostawcy', icon: Briefcase, hint: 'Kontakty i koszty usługodawców: sala, DJ, foto, dekoracje itd.' },
        { id: 'expenses', name: 'Wydatki', icon: DollarSign, hint: 'Pełna kontrola wydatków i budżetu ślubnego.' },
        { id: 'notes', name: 'Notatki', icon: Notebook, hint: 'Szybkie notatki i ustalenia, żeby nic nie uciekło.' },
    ], []);

    const categories = useMemo(() => ({
        all: { name: 'Wszystkie zadania', icon: Target, color: '#6366f1' },
        venue: { name: 'Miejsce', icon: MapPin, color: '#3b82f6' },
        catering: { name: 'Catering', icon: Utensils, color: '#10b981' },
        attire: { name: 'Stroje', icon: Shirt, color: '#8b5cf6' },
        music: { name: 'Muzyka', icon: Music, color: '#ef4444'},
        other: { name: 'Inne', icon: Sparkles, color: '#f59e0b'},
    }), []);
     
    const decorationTypes = useMemo(() => [
        { id: 'dance-floor', name: 'Parkiet Taneczny', icon: Music, color: '#8b5cf6', defaultSize: { width: 200, height: 200 } },
        { id: 'dj', name: 'Stanowisko DJ', icon: Disc, color: '#ef4444', defaultSize: { width: 120, height: 80 } },
        { id: 'bar', name: 'Bar', icon: Coffee, color: '#06b6d4', defaultSize: { width: 150, height: 60 } },
    ], []);

    const toggleGuestField = useCallback((guestId, field) => {
        setAppState.setGuests(prevGuests => {
            return prevGuests.map(guest => {
                if (guest.id === guestId) {
                    let newValue;
                    if (['wedding', 'afterparty', 'confirmed'].includes(field)) {
                        const cycle = { true: false, false: null, null: true };
                        newValue = cycle[String(guest[field])];
                    } else {
                        newValue = !guest[field];
                    }
                    return { ...guest, [field]: newValue };
                }
                return guest;
            });
        });
    }, [setAppState]);

    const assignGuestToSeat = useCallback((guestId, tableId, seatNumber) => {
        setAppState.setGuests(prevGuests => {
            const guestToAssign = prevGuests.find(g => g.id === guestId);
            const currentGuestOnSeat = prevGuests.find(g => g.tableId === tableId && g.seatNumber === seatNumber);

            return prevGuests.map(guest => {
                if(guest.id === guestId) return { ...guest, tableId, seatNumber, tableAssigned: true };
                if(currentGuestOnSeat && guest.id === currentGuestOnSeat.id) return { ...guest, tableId: guestToAssign.tableId, seatNumber: guestToAssign.seatNumber, tableAssigned: !!guestToAssign.tableId };
                return guest;
            });
        });
    }, [setAppState]);

    const removeGuestFromSeat = useCallback((guestId) => {
        setAppState.setGuests(prevGuests => prevGuests.map(guest => 
            guest.id === guestId ? { ...guest, tableId: null, seatNumber: null, tableAssigned: false } : guest
        ));
    }, [setAppState]);

    const addTable = useCallback((shape = 'circle', seats = 8) => {
        const newId = crypto.randomUUID();
        const newTable = {
            id: newId,
            name: `Stół ${tables.length + 1}`,
            x: 700, y: 500, seats, shape, rotation: 0, color: '#3b82f6', isLocked: false,
            radius: shape === 'circle' ? 80 : null,
            width: shape === 'rectangle' ? 160 : null,
            height: shape === 'rectangle' ? 80 : null,
        };
        setAppState.setTables(prev => [...prev, newTable]);
        setSelectedItem({ type: 'table', id: newId });
    }, [tables.length, setAppState]);

    const addDecoration = useCallback((type) => {
        const decorationType = decorationTypes.find(dt => dt.id === type);
        const newId = crypto.randomUUID();
        const newDecoration = {
            id: newId, name: decorationType.name,
            x: 700, y: 500, width: decorationType.defaultSize.width,
            height: decorationType.defaultSize.height,
            type, color: decorationType.color, rotation: 0, isLocked: false,
        };
        setAppState.setDecorations(prev => [...prev, newDecoration]);
        setSelectedItem({ type: 'decoration', id: newId });
    }, [decorationTypes, setAppState]);

    const renderActiveSection = () => {
        switch (activeSection) {
            case 'dashboard':
                return <Dashboard dashboardStats={dashboardStats} setActiveSection={setAppState.setActiveSection} tasks={tasks} />;
            case 'seating':
                 return <SeatingPlan 
                    guests={guests} setGuests={setAppState.setGuests} 
                    tables={tables} setTables={setAppState.setTables} 
                    decorations={decorations} setDecorations={setAppState.setDecorations} 
                    selectedItem={selectedItem} setSelectedItem={setSelectedItem} 
                    showSeatPicker={showSeatPicker} setShowSeatPicker={setShowSeatPicker} 
                    selectedSeat={selectedSeat} setSelectedSeat={setSelectedSeat} 
                    showAddMenu={showAddMenu} setShowAddMenu={setShowAddMenu} 
                    guestListView={guestListView} setGuestListView={setGuestListView} 
                    isAiSeatingLoading={isAiSeatingLoading} setIsAiSeatingLoading={setIsAiSeatingLoading} 
                    darkMode={darkMode} showGrid={showGrid} setShowGrid={setShowGrid} 
                    showNotification={showNotification} 
                    assignGuestToSeat={assignGuestToSeat} removeGuestFromSeat={removeGuestFromSeat} 
                    addTable={addTable} addDecoration={addDecoration} 
                    decorationTypes={decorationTypes} 
                    inspectorState={inspectorState} setInspectorState={setInspectorState} 
                />;
            case 'guests':
                 return <GuestList 
                    guests={guests} setGuests={setAppState.setGuests} 
                    showNotification={showNotification} toggleGuestField={toggleGuestField}
                    sortField={sortField} setSortField={setSortField} 
                    sortDirection={sortDirection} setSortDirection={setSortDirection}
                />;
            case 'tasks':
                return <TasksManager tasks={tasks} setTasks={setAppState.setTasks} showNotification={showNotification} toggleTask={toggleTask} updateTask={updateTask} deleteTask={deleteTask} categories={categories} />;
            case 'vendors':
                return <VendorsManager vendors={vendors} setVendors={setAppState.setVendors} showNotification={showNotification} />;
            case 'expenses':
                return <ExpensesManager expenses={expenses} setExpenses={setAppState.setExpenses} weddingSettings={weddingSettings} showNotification={showNotification} />;
            case 'notes':
                return <NotesManager notes={notes} setNotes={setAppState.setNotes} />;
            default:
                return null;
        }
    };

    return (
        <div className={darkMode ? 'dark' : ''}>
            <div className="min-h-screen bg-cream-50 dark:bg-slate-900 text-gray-800 dark:text-gray-200 transition-colors duration-300 font-sans">
                <div className="sticky top-0 z-40">
                    <MainHeader 
                        darkMode={darkMode} 
                        setDarkMode={setDarkMode} 
                        dashboardStats={dashboardStats} 
                        onSettingsClick={() => setIsSettingsModalOpen(true)}
                        onOpenHelp={() => setIsHelpModalOpen(true)}
                        weddingSettings={weddingSettings}
                        currentUserName={currentUserName}
                        onSignOut={onSignOut}
                        onExportData={onExportData}
                        onImportData={onImportData}
                        isImportingData={isImportingData}
                    />
                    <NavigationTabs activeSection={activeSection} setActiveSection={setAppState.setActiveSection} navigationItems={navigationItems} />
                </div>
                <main className="max-w-screen-2xl mx-auto px-6 py-8">
                     {renderActiveSection()}
                </main>
            </div>
            <QuickHelpModal
                isOpen={isHelpModalOpen}
                onClose={() => setIsHelpModalOpen(false)}
                onDisableAutoOpen={disableHelpAutoOpen}
            />
             <SettingsModal 
                isOpen={isSettingsModalOpen}
                onClose={() => setIsSettingsModalOpen(false)}
                settings={weddingSettings}
                setSettings={setAppState.setWeddingSettings}
                showNotification={showNotification}
            />
        </div>
    );
};
// #endregion

// #region --- Main Wrapper Component & Data Management ---
const WeddingerAppWrapper = () => {
    const [notification, setNotification] = useState(null);
    const [darkMode, setDarkMode] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);
    const [isAuthLoading, setIsAuthLoading] = useState(true);
    const [isAuthSubmitting, setIsAuthSubmitting] = useState(false);
    const [isOnboardingSubmitting, setIsOnboardingSubmitting] = useState(false);
    const [isImportingLegacy, setIsImportingLegacy] = useState(false);
    const [isFeedbackSubmitting, setIsFeedbackSubmitting] = useState(false);
    const [isImportingData, setIsImportingData] = useState(false);

    const [currentUser, setCurrentUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [isProfileLoaded, setIsProfileLoaded] = useState(false);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [hasPlannerDoc, setHasPlannerDoc] = useState(false);
    const [appState, setAppState] = useState(createEmptyAppState());

    const debouncedState = useDebounce(appState, 120);
    const userProfileRef = useMemo(() => currentUser ? doc(db, "users", currentUser.uid, "meta", "profile") : null, [currentUser]);
    const userPlannerRef = useMemo(() => currentUser ? doc(db, "users", currentUser.uid, "planner", "mainState") : null, [currentUser]);
    const userBackupsCollectionRef = useMemo(() => currentUser ? collection(db, "users", currentUser.uid, "plannerBackups") : null, [currentUser]);
    const backupKey = useMemo(() => currentUser ? `weddinger_backup_${currentUser.uid}` : null, [currentUser]);
    const isCloudBackupInProgressRef = useRef(false);

    const showNotification = useCallback((message, type = 'success', duration = 3000) => {
        setNotification({ id: Date.now(), message, type });
        setTimeout(() => setNotification(null), duration);
    }, []);

    const handleShowConfetti = useCallback(() => {
        setShowConfetti(false);
        setTimeout(() => setShowConfetti(true), 10);
        setTimeout(() => setShowConfetti(false), 5000);
    }, []);

    const saveLocalBackup = useCallback((stateToSave) => {
        if (!backupKey) return;
        try {
            localStorage.setItem(backupKey, JSON.stringify({
                savedAt: new Date().toISOString(),
                state: stateToSave,
            }));
        } catch (error) {
            console.error("Local backup save failed:", error);
        }
    }, [backupKey]);

    const createCloudBackup = useCallback(async (stateToSave, source = 'auto') => {
        if (!userBackupsCollectionRef || !userProfileRef || isCloudBackupInProgressRef.current) return;
        isCloudBackupInProgressRef.current = true;
        const now = new Date().toISOString();
        try {
            await addDoc(userBackupsCollectionRef, {
                createdAt: now,
                source,
                state: stateToSave,
            });
            await setDoc(userProfileRef, { lastCloudBackupAt: now }, { merge: true });
            setProfile(prev => ({ ...(prev || {}), lastCloudBackupAt: now }));
        } finally {
            isCloudBackupInProgressRef.current = false;
        }
    }, [userBackupsCollectionRef, userProfileRef]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
            setIsAuthLoading(false);
        }, (error) => {
            console.error("Firebase auth failed: ", error);
            showNotification("Błąd autoryzacji Firebase.", 'error');
            setIsAuthLoading(false);
        });
        return () => {
            if (typeof unsubscribe === 'function') unsubscribe();
        };
    }, [showNotification]);

    useEffect(() => {
        if (!currentUser) {
            setProfile(null);
            setIsProfileLoaded(false);
            setHasPlannerDoc(false);
            setIsDataLoaded(false);
            setAppState(createEmptyAppState());
        }
    }, [currentUser]);

    useEffect(() => {
        if (!userProfileRef) return;
        const unsubscribe = onSnapshot(userProfileRef, (docSnap) => {
            if (docSnap.exists()) {
                setProfile(docSnap.data());
            } else {
                setProfile(null);
            }
            setIsProfileLoaded(true);
        }, (error) => {
            console.error("Firebase profile read failed:", error);
            showNotification("Nie można pobrać profilu użytkownika.", 'error');
            setIsProfileLoaded(true);
        });
        return () => unsubscribe();
    }, [userProfileRef, showNotification]);

    useEffect(() => {
        if (!userPlannerRef) return;
        const unsubscribe = onSnapshot(userPlannerRef, (docSnap) => {
            if (docSnap.exists()) {
                const mergedState = mergeWithDefaultAppState(docSnap.data());
                setHasPlannerDoc(true);
                setAppState(mergedState);
                saveLocalBackup(mergedState);
            } else {
                let restoredState = null;
                if (backupKey) {
                    try {
                        const rawBackup = localStorage.getItem(backupKey);
                        if (rawBackup) {
                            const parsed = JSON.parse(rawBackup);
                            if (parsed?.state) {
                                restoredState = mergeWithDefaultAppState(parsed.state);
                            }
                        }
                    } catch (error) {
                        console.error("Local backup read failed:", error);
                    }
                }

                if (restoredState) {
                    setHasPlannerDoc(true);
                    setAppState(restoredState);
                    setDoc(userPlannerRef, restoredState, { merge: false }).then(() => {
                        showNotification("Przywrócono lokalną kopię danych.", 'info', 5000);
                    }).catch((error) => {
                        console.error("Planner restore from local backup failed:", error);
                    });
                } else {
                    setHasPlannerDoc(false);
                    setAppState(createEmptyAppState());
                }
            }
            setIsDataLoaded(true);
        }, (error) => {
            console.error("Firebase planner read failed:", error);
            showNotification("Błąd połączenia z planerem.", 'error');
            setIsDataLoaded(true);
        });
        return () => unsubscribe();
    }, [backupKey, saveLocalBackup, showNotification, userPlannerRef]);

    useEffect(() => {
        if (!currentUser || !userProfileRef || !isProfileLoaded) return;
        if (!hasPlannerDoc || profile?.onboardingCompleted) return;
        const now = new Date().toISOString();
        setDoc(userProfileRef, {
            displayName: profile?.displayName || currentUser.displayName || "",
            role: profile?.role || "couple",
            onboardingCompleted: true,
            createdAt: profile?.createdAt || now,
            updatedAt: now,
        }, { merge: true }).catch((error) => {
            console.error("Firebase profile auto-heal failed:", error);
        });
    }, [currentUser, userProfileRef, isProfileLoaded, hasPlannerDoc, profile]);

    useEffect(() => {
        if (!isDataLoaded || !userPlannerRef) return;
        if (!hasPlannerDoc && !profile?.onboardingCompleted) return;
        // Prevent stale debounced value from overwriting freshly hydrated state after refresh.
        if (debouncedState !== appState) return;
        setDoc(userPlannerRef, debouncedState, { merge: true }).then(() => {
            saveLocalBackup(debouncedState);
            const lastBackupAtMs = profile?.lastCloudBackupAt ? new Date(profile.lastCloudBackupAt).getTime() : 0;
            const shouldCreateAutoCloudBackup = !lastBackupAtMs || (Date.now() - lastBackupAtMs) > AUTO_CLOUD_BACKUP_INTERVAL_MS;
            if (shouldCreateAutoCloudBackup) {
                createCloudBackup(debouncedState, 'auto').catch((error) => {
                    console.error("Auto cloud backup failed:", error);
                });
            }
        }).catch((error) => {
            console.error("Firebase planner write failed:", error);
            showNotification(`Błąd zapisu danych planera (${error?.code || 'unknown'}).`, 'error');
        });
    }, [appState, createCloudBackup, debouncedState, hasPlannerDoc, isDataLoaded, profile?.lastCloudBackupAt, profile?.onboardingCompleted, saveLocalBackup, showNotification, userPlannerRef]);

    useEffect(() => {
        if (!currentUser || !isDataLoaded) return;
        saveLocalBackup(appState);
    }, [appState, currentUser, isDataLoaded, saveLocalBackup]);

    useEffect(() => {
        if (!currentUser || !isDataLoaded) return;
        const persistBeforeLeave = () => saveLocalBackup(appState);
        window.addEventListener('beforeunload', persistBeforeLeave);
        window.addEventListener('pagehide', persistBeforeLeave);
        return () => {
            window.removeEventListener('beforeunload', persistBeforeLeave);
            window.removeEventListener('pagehide', persistBeforeLeave);
        };
    }, [appState, currentUser, isDataLoaded, saveLocalBackup]);

    const parseAuthError = (error) => {
        if (!error?.code) return "Wystąpił błąd logowania.";
        const authErrors = {
            'auth/invalid-credential': 'Nieprawidłowy email lub hasło.',
            'auth/email-already-in-use': 'Ten email jest już zajęty.',
            'auth/weak-password': 'Hasło jest za słabe.',
            'auth/invalid-email': 'Niepoprawny format email.',
            'auth/too-many-requests': 'Za dużo prób. Spróbuj ponownie później.',
            'auth/invalid-api-key': 'Błąd konfiguracji Firebase (API key).',
            'auth/app-not-authorized': 'Aplikacja nie ma dostępu do Firebase. Sprawdź konfigurację projektu.',
            'auth/unauthorized-domain': 'Ta domena nie jest dozwolona w Firebase Authentication.',
            'auth/operation-not-allowed': 'Logowanie email/hasło nie jest włączone w Firebase Authentication.',
            'auth/network-request-failed': 'Błąd połączenia z internetem. Spróbuj ponownie.',
            'auth/configuration-not-found': 'Brak konfiguracji Firebase Authentication dla tego projektu.',
            'auth/user-disabled': 'To konto jest zablokowane.',
        };
        return authErrors[error.code] || `Nie udało się wykonać operacji na koncie (${error.code}).`;
    };

    const handleLogin = useCallback(async ({ email, password }) => {
        setIsAuthSubmitting(true);
        try {
            await signInWithEmailAndPassword(auth, email, password);
            showNotification('Zalogowano pomyślnie.', 'success');
        } catch (error) {
            throw new Error(parseAuthError(error));
        } finally {
            setIsAuthSubmitting(false);
        }
    }, [showNotification]);

    const handleRegister = useCallback(async ({ fullName, email, password }) => {
        setIsAuthSubmitting(true);
        try {
            const credentials = await createUserWithEmailAndPassword(auth, email, password);
            if (fullName) {
                await updateProfile(credentials.user, { displayName: fullName });
            }
            const now = new Date().toISOString();
            await setDoc(doc(db, "users", credentials.user.uid, "meta", "profile"), {
                displayName: fullName || "",
                role: "couple",
                onboardingCompleted: false,
                createdAt: now,
                updatedAt: now,
            }, { merge: true });
            showNotification('Konto utworzone. Czas na konfigurację planera.', 'success');
        } catch (error) {
            throw new Error(parseAuthError(error));
        } finally {
            setIsAuthSubmitting(false);
        }
    }, [showNotification]);

    const handleSignOut = useCallback(async () => {
        try {
            await signOut(auth);
            showNotification('Wylogowano.', 'info');
        } catch (error) {
            console.error("Sign out failed:", error);
            showNotification('Nie udało się wylogować.', 'error');
        }
    }, [showNotification]);

    const handleExportData = useCallback(() => {
        if (!currentUser || !hasPlannerDoc) {
            showNotification('Najpierw utwórz planer, potem pobierz dane.', 'info');
            return;
        }
        try {
            const payload = {
                version: 1,
                exportedAt: new Date().toISOString(),
                exportedBy: currentUser.email || null,
                state: appState,
            };
            const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            const fileDate = new Date().toISOString().slice(0, 10);
            anchor.href = url;
            anchor.download = `weddinger-backup-${fileDate}.json`;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            URL.revokeObjectURL(url);
            showNotification('Backup danych został pobrany.', 'success');
        } catch (error) {
            console.error("Manual data export failed:", error);
            showNotification('Nie udało się pobrać backupu danych.', 'error');
        }
    }, [appState, currentUser, hasPlannerDoc, showNotification]);

    const handleImportData = useCallback(async (file) => {
        if (!file) return;
        if (!currentUser || !userPlannerRef) {
            showNotification('Musisz być zalogowany, aby wgrać backup.', 'error');
            return;
        }
        setIsImportingData(true);
        try {
            const rawText = await file.text();
            const parsed = JSON.parse(rawText);
            const rawState = parsed?.state ? parsed.state : parsed;
            if (!rawState || typeof rawState !== 'object') {
                throw new Error('Niepoprawny format pliku backupu.');
            }

            const restoredState = mergeWithDefaultAppState(rawState);
            await setDoc(userPlannerRef, restoredState, { merge: false });
            setAppState(restoredState);
            setHasPlannerDoc(true);
            saveLocalBackup(restoredState);
            showNotification('Backup został wgrany i zapisany.', 'success', 5000);
        } catch (error) {
            console.error("Manual data import failed:", error);
            showNotification(error?.message || 'Nie udało się wgrać backupu.', 'error');
        } finally {
            setIsImportingData(false);
        }
    }, [currentUser, saveLocalBackup, showNotification, userPlannerRef]);

    const handleCompleteOnboarding = useCallback(async (formData) => {
        if (!currentUser || !userProfileRef || !userPlannerRef) return;
        setIsOnboardingSubmitting(true);
        try {
            const nextState = buildStateFromOnboarding(formData);
            const now = new Date().toISOString();

            await setDoc(userPlannerRef, nextState, { merge: true });
            await setDoc(userProfileRef, {
                displayName: formData.yourName || currentUser.displayName || "",
                role: formData.yourRole || "couple",
                onboardingCompleted: true,
                createdAt: profile?.createdAt || now,
                updatedAt: now,
            }, { merge: true });

            if (formData.yourName && formData.yourName !== currentUser.displayName) {
                await updateProfile(currentUser, { displayName: formData.yourName });
            }

            setAppState(nextState);
            saveLocalBackup(nextState);
            setHasPlannerDoc(true);
            setProfile((prev) => ({
                ...(prev || {}),
                displayName: formData.yourName || currentUser.displayName || "",
                role: formData.yourRole || "couple",
                onboardingCompleted: true,
                createdAt: prev?.createdAt || now,
                updatedAt: now,
            }));
            showNotification('Planer gotowy. Możesz zaczynać!', 'success');
        } catch (error) {
            console.error("Onboarding failed:", error);
            showNotification('Nie udało się zapisać onboardingu.', 'error');
        } finally {
            setIsOnboardingSubmitting(false);
        }
    }, [currentUser, profile?.createdAt, saveLocalBackup, showNotification, userPlannerRef, userProfileRef]);

    const handleSubmitFeedback = useCallback(async (payload) => {
        setIsFeedbackSubmitting(true);
        try {
            const response = await fetch('/api/feedback', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...payload,
                    userId: currentUser?.uid || null,
                    userEmail: currentUser?.email || null,
                }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data?.error || 'Nie udało się wysłać wiadomości.');
            }
            showNotification('Dzięki! Wiadomość została wysłana.', 'success');
            return true;
        } catch (error) {
            console.error("Feedback send failed:", error);
            showNotification(error?.message || 'Nie udało się wysłać feedbacku.', 'error');
            return false;
        } finally {
            setIsFeedbackSubmitting(false);
        }
    }, [currentUser, showNotification]);

    const ownerEmailForLegacyImport = process.env.REACT_APP_LEGACY_OWNER_EMAIL || "";
    const canImportLegacy = Boolean(
        currentUser?.email &&
        ownerEmailForLegacyImport &&
        currentUser.email.toLowerCase() === ownerEmailForLegacyImport.toLowerCase() &&
        !hasPlannerDoc
    );

    const handleImportLegacy = useCallback(async () => {
        if (!currentUser || !userPlannerRef || !userProfileRef || !canImportLegacy) return;
        setIsImportingLegacy(true);
        try {
            const legacyRef = doc(db, LEGACY_DATA_COLLECTION, LEGACY_DATA_DOC);
            const legacySnapshot = await getDoc(legacyRef);
            if (!legacySnapshot.exists()) {
                showNotification('Brak danych legacy do importu.', 'info');
                return;
            }

            const migratedState = mergeWithDefaultAppState(legacySnapshot.data());
            const now = new Date().toISOString();

            await setDoc(userPlannerRef, migratedState, { merge: false });
            await setDoc(userProfileRef, {
                displayName: profile?.displayName || currentUser.displayName || "",
                role: profile?.role || "couple",
                onboardingCompleted: true,
                migratedFromLegacy: true,
                createdAt: profile?.createdAt || now,
                updatedAt: now,
            });
            setHasPlannerDoc(true);
            setAppState(migratedState);
            saveLocalBackup(migratedState);
            setProfile((prev) => ({
                ...(prev || {}),
                displayName: prev?.displayName || currentUser.displayName || "",
                role: prev?.role || "couple",
                onboardingCompleted: true,
                migratedFromLegacy: true,
                createdAt: prev?.createdAt || now,
                updatedAt: now,
            }));
            showNotification('Dane legacy zostały bezpiecznie przeniesione.', 'success', 5000);
        } catch (error) {
            console.error("Legacy import failed:", error);
            showNotification('Import legacy nie powiódł się.', 'error');
        } finally {
            setIsImportingLegacy(false);
        }
    }, [canImportLegacy, currentUser, profile?.createdAt, profile?.displayName, profile?.role, saveLocalBackup, showNotification, userPlannerRef, userProfileRef]);


    const stateSetters = {
        setGuests: (updater) => setAppState(prev => ({ ...prev, guests: typeof updater === 'function' ? updater(prev.guests) : updater })),
        setTables: (updater) => setAppState(prev => ({ ...prev, tables: typeof updater === 'function' ? updater(prev.tables) : updater })),
        setDecorations: (updater) => setAppState(prev => ({ ...prev, decorations: typeof updater === 'function' ? updater(prev.decorations) : updater })),
        setTasks: (updater) => setAppState(prev => ({ ...prev, tasks: typeof updater === 'function' ? updater(prev.tasks) : updater })),
        setVendors: (updater) => setAppState(prev => ({ ...prev, vendors: typeof updater === 'function' ? updater(prev.vendors) : updater })),
        setExpenses: (updater) => setAppState(prev => ({ ...prev, expenses: typeof updater === 'function' ? updater(prev.expenses) : updater })),
        setNotes: (updater) => setAppState(prev => ({ ...prev, notes: typeof updater === 'function' ? updater(prev.notes) : updater })),
        setActiveSection: (updater) => setAppState(prev => ({ ...prev, activeSection: typeof updater === 'function' ? updater(prev.activeSection) : updater })),
        setWeddingSettings: (updater) => setAppState(prev => ({...prev, weddingSettings: typeof updater === 'function' ? updater(prev.weddingSettings) : updater })),
    };
    
    const NotificationComponent = () => {
        if (!notification) return null;
        const config = {
             success: { bg: 'bg-gradient-to-r from-green-500 to-emerald-500', icon: Check },
             error: { bg: 'bg-gradient-to-r from-red-500 to-rose-500', icon: AlertTriangle },
             info: { bg: 'bg-gradient-to-r from-blue-500 to-indigo-500', icon: Target },
        };
        const { bg, icon: Icon } = config[notification.type] || config.info;
        return (
             <div key={notification.id} className={`fixed bottom-6 right-6 ${bg} text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 z-[200]`}>
                 <Icon size={20} />
                 <p className="font-semibold text-sm">{notification.message}</p>
             </div>
        );
    };

    if (isAuthLoading) {
        return (
             <>
                 <div className="w-full h-screen flex flex-col items-center justify-center bg-cream-50 dark:bg-slate-900">
                     <Heart className="w-16 h-16 text-rose-500 dark:text-rose-400 animate-pulse mb-4" />
                     <h1 className="text-2xl font-bold text-gray-700 dark:text-gray-300">Sprawdzam sesję konta...</h1>
                 </div>
             </>
        )
    }

    if (!currentUser) {
        return (
            <>
                <AuthLanding
                    onLogin={handleLogin}
                    onRegister={handleRegister}
                    isSubmitting={isAuthSubmitting}
                />
                <NotificationComponent />
            </>
        );
    }

    if (!isProfileLoaded || !isDataLoaded) {
        return (
            <>
                <div className="w-full h-screen flex flex-col items-center justify-center bg-cream-50 dark:bg-slate-900">
                    <Heart className="w-16 h-16 text-rose-500 dark:text-rose-400 animate-pulse mb-4" />
                    <h1 className="text-2xl font-bold text-gray-700 dark:text-gray-300">Ładowanie Twojego planera...</h1>
                </div>
                <FeedbackWidget
                    onSubmit={handleSubmitFeedback}
                    isSubmitting={isFeedbackSubmitting}
                    currentUserEmail={currentUser?.email || ""}
                />
                <NotificationComponent />
            </>
        );
    }

    if (!hasPlannerDoc) {
        return (
            <>
                <OnboardingWizard
                    defaultName={profile?.displayName || currentUser.displayName || ""}
                    onComplete={handleCompleteOnboarding}
                    isSubmitting={isOnboardingSubmitting}
                    canImportLegacy={canImportLegacy}
                    onImportLegacy={handleImportLegacy}
                    isImportingLegacy={isImportingLegacy}
                />
                <FeedbackWidget
                    onSubmit={handleSubmitFeedback}
                    isSubmitting={isFeedbackSubmitting}
                    currentUserEmail={currentUser?.email || ""}
                />
                <NotificationComponent />
            </>
        );
    }

    return (
        <ErrorBoundary showNotification={showNotification}>
            <CustomConfetti isFiring={showConfetti} />
            <WeddingerApp 
                showNotification={showNotification}
                appState={appState}
                setAppState={stateSetters}
                darkMode={darkMode}
                setDarkMode={setDarkMode}
                onShowConfetti={handleShowConfetti}
                currentUserName={profile?.displayName || currentUser.displayName || currentUser.email || 'Konto'}
                currentUserId={currentUser?.uid || ''}
                onSignOut={handleSignOut}
                onExportData={handleExportData}
                onImportData={handleImportData}
                isImportingData={isImportingData}
            />
            <FeedbackWidget
                onSubmit={handleSubmitFeedback}
                isSubmitting={isFeedbackSubmitting}
                currentUserEmail={currentUser?.email || ""}
            />
            <NotificationComponent />
        </ErrorBoundary>
    )
}

export default WeddingerAppWrapper;
// #endregion
